// Updated architecture-tab-layout.tsx to accept graph data from parent
import {
  Fragment,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Play,
  ChevronRight,
  ChevronLeft,
  Save,
  X,
  Sparkles,
  Eye,
  AlertCircle,
} from "lucide-react";
import { Pagination } from "../elements/pagination";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import { CitationList, PersonaCard } from "../elements/card-group";
import { Badge } from "../ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../ui/accordion";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { useGetNodeCitations } from "@/service-hooks/use-get-node-citations-service";
import { NodeCitation } from "@/service-hooks/use-get-functional-graph-service";
import { getDocumentsSignedUrlById } from "@/service-hooks/use-get-document-service";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { useQueryClient } from "@tanstack/react-query";
import { useArchitecture } from "@/hooks/use-architecture";
import ArchitecturePopupEditor from "../elements/architecture-service-editor";
import { ArchitectureNodeDialog } from "../elements/architecture-node-dialog";
import ConfirmDialog from "../elements/confirm-dialog";
import CustomTooltip from "../elements/custom-tooltip";
import EsOntologySection from "../elements/es-ontology-section";
import NoSqlOntologySection from "../elements/nosql-ontology-section";
import GraphDbOntologySection from "../elements/graph-db-ontology-section";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { useProjectRole } from "@/context/project-role-context";
import { useUser } from "@/hooks/use-user";
import { useRegenerateArchitectureEmbeddings } from "@/service-hooks/use-regenerate-architecture-embeddings-service";
import {
  ArchitectureComponent,
  ArchitectureTabLayoutProps,
} from "@/types/unified-model";
import {
  categoryToLabel,
  transformGraphNodeToComponent,
  useNeo4jGraphService,
} from "@/service-hooks/use-neo4j-graph-service";
import {
  DbOntologyConstraint,
  DbOntologyIndex,
  DbOntologyProcedure,
  DbOntologyView,
  useCreateDbOntologyConstraint,
  useDeleteDbOntologyColumn,
  useDeleteDbOntologyConstraint,
  useDeleteDbOntologyIndex,
  useDeleteDbOntologyProcedure,
  useDeleteDbOntologyTable,
  useDeleteDbOntologyView,
  useGetDbOntologyColumns,
  useGetDbOntologyConstraints,
  useGetDbOntologyIndexes,
  useGetDbOntologyProcedures,
  useDataLakeIngestionStatus,
  useGetDbOntologyTables,
  useGetDbOntologyViews,
  useStreamIngestSql,
  useUpdateDbOntologyNode,
} from "@/service-hooks/use-get-db-ontology-service";
import DbOntologyTableDialog from "../elements/db-ontology-table-dialog";
import DbOntologyColumnDialog from "../elements/db-ontology-column-dialog";
import DbOntologyViewDialog from "../elements/db-ontology-view-dialog";
import DbOntologyProcedureDialog from "../elements/db-ontology-procedure-dialog";
import DbOntologyIndexDialog from "../elements/db-ontology-index-dialog";
import DbOntologyConstraintDialog from "../elements/db-ontology-constraint-dialog";

/** Id of the sr-only hint describing why Visualize Architecture is disabled. */
const VISUALIZE_HINT_ID = "visualize-architecture-hint";

interface Column {
  id?: string;
  name: string;
  type: string;
  nullable?: boolean;
  primaryKey?: boolean;
  description?: string;
}

interface Table {
  id?: string;
  name: string;
  description?: string;
  columns: Column[];
  columnCount?: number;
}

interface CategoryPagination {
  page: number;
  limit: number;
  hasMore: boolean;
  loading: boolean;
  totalPages: number;
}

interface ArchitectureTabLayoutPropsExtended extends ArchitectureTabLayoutProps {
  projectUuid: string;
  graphData?: any; // Graph data from parent
  isLoadingGraph?: boolean; // Loading state from parent
  onVisualizeArchitectureClick?: () => void;
  hasFunctionalData?: boolean;
}

export default function ArchitectureTabLayout({
  setTabs,
  projectUuid,
  graphData, // Accept from parent
  isLoadingGraph, // Accept from parent
  onVisualizeArchitectureClick,
}: ArchitectureTabLayoutPropsExtended) {
  // Pass graph data and loading state to useArchitecture hook
  const { selectedEntity, handleUpdateSingleComponent, loadCategoryData } =
    useArchitecture(
      graphData, // Pass through graph data
      isLoadingGraph, // Pass through loading state
    );

  // Initialize Neo4j CRUD services
  const { useCreateNode, useUpdateNode, useDeleteNode } =
    useNeo4jGraphService(projectUuid);
  const createNodeMutation = useCreateNode();
  const updateNodeMutation = useUpdateNode();
  const deleteNodeMutation = useDeleteNode();
  const { isViewer } = useProjectRole();
  const { user } = useUser();
  const isAdmin = user?.role === "ADMIN";

  // Regenerate embeddings for architectural graph
  const { mutate: regenerateArchitectureEmbeddings } =
    useRegenerateArchitectureEmbeddings(
      () => {
        toast.success("Embedding regeneration started successfully.");
      },
      (error) => {
        toast.error(error.message || "Failed to regenerate embeddings.");
      },
    );
  const [isEmbeddingConfirmOpen, setEmbeddingConfirmOpen] = useState(false);

  // Popup editor state
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedComponent] = useState<ArchitectureComponent | null>(null);

  // Dialog states for edit/delete functionality
  const [isNodeDialogOpen, setNodeDialogOpen] = useState(false);
  const [currentNode, setCurrentNode] = useState<ArchitectureComponent | null>(
    null,
  );
  const [currentCategory, setCurrentCategory] = useState("");
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState<{
    node: ArchitectureComponent;
    category: string;
  } | null>(null);
  // Unified pending-delete state for Data Lake Table / Column / Index rows.
  // When set, the ConfirmDialog below prompts the user before invoking `execute`.
  // Dialog auto-closes on Confirm, so the destructive handler cannot be spam-clicked.
  const [pendingDataLakeDelete, setPendingDataLakeDelete] = useState<{
    type: "Table" | "Column" | "Index" | "Constraint" | "View" | "Procedure";
    name: string;
    execute: () => Promise<void> | void;
  } | null>(null);

  const [expandedCategory, setExpandedCategoryRaw] = useState<string | null>(
    null,
  );
  const expandedCategoryRef = useRef<string | null>(null);
  const setExpandedCategory = (val: string | null) => {
    expandedCategoryRef.current = val;
    setExpandedCategoryRaw(val);
  };

  // Citation display state
  const [selectedArchNode, setSelectedArchNode] =
    useState<ArchitectureComponent | null>(null);
  const [selectedTableIndex, setSelectedTableIndex] = useState<number | null>(
    null,
  );
  const [tablesPagination, setTablesPagination] = useState({
    page: 1,
    limit: 5,
  });
  const [columnsPagination, setColumnsPagination] = useState({
    page: 1,
    limit: 5,
  });
  const [editingTable, setEditingTable] = useState<number | null>(null);
  const [editingColumn, setEditingColumn] = useState<{
    tableIndex: number;
    columnIndex: number;
  } | null>(null);
  const [editedTableData, setEditedTableData] = useState<Table | null>(null);
  const [editedColumnData, setEditedColumnData] = useState<Column | null>(null);

  const { getAuthHeaders } = useAuthHeaders();
  const citationsQueryClient = useQueryClient();
  const { data: archNodeCitations } = useGetNodeCitations(
    selectedArchNode?.citationRefs,
    projectUuid,
  );

  const isSelectedDataLake = selectedArchNode?.group === "data_lake";
  const dataLakeNodeId = isSelectedDataLake ? selectedArchNode?.id : undefined;
  // Type is set on the data_lake blueprint ("Elastic" | "Relational" | "Non-Relational").
  //   - Elastic       → ES panel only.
  //   - Relational    → DDL panels only.
  //   - Non-Relational → NoSQL collections panel (manual CRUD + JSON-schema upload).
  // When type is unset we fall back to DDL panels for back-compat with
  // existing data lakes created before the type field was introduced.
  const selectedDataLakeType =
    (selectedArchNode as any)?.metadata?.blueprint?.type ??
    (selectedArchNode as any)?.type;
  const isSelectedDataLakeElastic =
    isSelectedDataLake && selectedDataLakeType === "Elastic";
  const isSelectedDataLakeNonRelational =
    isSelectedDataLake && selectedDataLakeType === "Non-Relational";
  //   - Graph → graph-database schema card (node types, edge types, indexes).
  const isSelectedDataLakeGraph =
    isSelectedDataLake && selectedDataLakeType === "Graph";
  const showDdlPanels =
    isSelectedDataLake &&
    !isSelectedDataLakeElastic &&
    !isSelectedDataLakeNonRelational &&
    !isSelectedDataLakeGraph;
  // Suspend DDL fetches for Elastic data lakes — they would return nothing
  // and add network noise on every selection.
  const [viewsPagination, setViewsPagination] = useState({
    page: 1,
    limit: 10,
  });
  const [proceduresPagination, setProceduresPagination] = useState({
    page: 1,
    limit: 10,
  });
  // Poll the selected DataLake's ingestionStatus while the hosted generator
  // parses the uploaded .sql/.json file. The architectural_specs response
  // already carries ingestionStatus/ingestionError, so initialStatus seeds
  // the banner on page refresh without waiting for the first poll.
  const { status: ingestionStatus, error: ingestionError } =
    useDataLakeIngestionStatus(
      projectUuid,
      isSelectedDataLake ? selectedArchNode?.id : undefined,
      (selectedArchNode as any)?.ingestionStatus ??
        (selectedArchNode as any)?.metadata?.blueprint?.ingestionStatus,
    );

  const { data: dbOntologyTablesPage, isFetching: isFetchingDbOntology } =
    useGetDbOntologyTables(dataLakeNodeId, showDdlPanels, {
      projectUuid,
      limit: tablesPagination.limit,
      page: tablesPagination.page,
    });
  // Wide fetch for dialogs that need to pick a table (FK targets, index parents).
  // Kept behind a separate query key so the paginated panel above isn't affected.
  const { data: dbOntologyTablesForRef } = useGetDbOntologyTables(
    dataLakeNodeId,
    showDdlPanels,
    { projectUuid, limit: 5000, page: 1 },
  );
  const { data: dbOntologyViewsPage, isFetching: isFetchingDbOntologyViews } =
    useGetDbOntologyViews(dataLakeNodeId, showDdlPanels, {
      projectUuid,
      limit: viewsPagination.limit,
      page: viewsPagination.page,
    });
  const {
    data: dbOntologyProceduresPage,
    isFetching: isFetchingDbOntologyProcedures,
  } = useGetDbOntologyProcedures(dataLakeNodeId, showDdlPanels, {
    projectUuid,
    limit: proceduresPagination.limit,
    page: proceduresPagination.page,
  });
  // Indexes/Constraints fetched scoped to the selected table — wired further down
  // after `selectedTableForColumns` is computed.
  const deleteDbOntologyIndex = useDeleteDbOntologyIndex();
  const deleteDbOntologyConstraint = useDeleteDbOntologyConstraint();
  const createDbOntologyConstraint = useCreateDbOntologyConstraint();
  const [indexesPagination, setIndexesPagination] = useState({
    page: 1,
    limit: 5,
  });
  const [constraintsPagination, setConstraintsPagination] = useState({
    page: 1,
    limit: 5,
  });
  const deleteDbOntologyTable = useDeleteDbOntologyTable();
  const deleteDbOntologyColumn = useDeleteDbOntologyColumn();
  const updateDbOntologyNode = useUpdateDbOntologyNode();
  // Save-in-progress flags for the inline DDL editors, so their Save buttons
  // can disable + show a spinner (matches the ES field dialog's behaviour).
  const isSavingColumn =
    updateDbOntologyNode.isPending ||
    createDbOntologyConstraint.isPending ||
    deleteDbOntologyConstraint.isPending ||
    updateNodeMutation.isPending;
  const isSavingTable =
    updateDbOntologyNode.isPending || updateNodeMutation.isPending;
  const [isCreateTableOpen, setCreateTableOpen] = useState(false);
  const [isCreateColumnOpen, setCreateColumnOpen] = useState(false);
  const [isViewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingView, setEditingView] = useState<DbOntologyView | null>(null);
  const [isProcedureDialogOpen, setProcedureDialogOpen] = useState(false);
  const [editingProcedure, setEditingProcedure] =
    useState<DbOntologyProcedure | null>(null);
  const [isIndexDialogOpen, setIndexDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<DbOntologyIndex | null>(
    null,
  );
  const [isConstraintDialogOpen, setConstraintDialogOpen] = useState(false);
  const [editingConstraint, setEditingConstraint] =
    useState<DbOntologyConstraint | null>(null);
  const deleteDbOntologyView = useDeleteDbOntologyView();
  const deleteDbOntologyProcedure = useDeleteDbOntologyProcedure();
  const streamIngestSql = useStreamIngestSql();

  const handleNodeCitationClick = async (citation: NodeCitation) => {
    const citationType = citation.type;

    if (
      citationType === "code" ||
      citationType === "prompt" ||
      citationType === "user"
    )
      return;

    if (
      citationType === "jira" ||
      citationType === "figma" ||
      citationType === "confluence"
    ) {
      window.open(citation.reference, "_blank", "noopener,noreferrer");
      return;
    }

    const docId = citation.reference;
    if (!docId) return;
    try {
      const signedUrl = await citationsQueryClient.fetchQuery({
        queryKey: ["getSignedDocUrl", docId],
        queryFn: () => {
          const headers = getAuthHeaders();
          return getDocumentsSignedUrlById(docId, headers);
        },
        staleTime: 300000,
      });
      window.open(signedUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  // Pagination state per category
  const [categoryPagination, setCategoryPagination] = useState<
    Record<string, CategoryPagination>
  >({});

  // Store current page data separately for display
  const [currentPageData, setCurrentPageData] = useState<
    Record<string, ArchitectureComponent[]>
  >({});

  // Category counts from main API
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>(
    {},
  );

  const allPossibleCategories = useMemo(
    () => [
      "user_experience",
      "api_gateway",
      "observability_monitoring",
      "agents",
      "services",
      "event_queue",
      "data_lake",
      "infrastructure",
    ],
    [],
  );

  // Use ref to avoid dependency issues
  const loadCategoryDataRef = useRef<
    | ((
        category: string,
        page?: number,
        skipLoadingGuard?: boolean,
      ) => Promise<void>)
    | null
  >(null);

  // Internal method to load category-specific data with proper pagination
  const loadCategoryDataInternal = useCallback(
    async (
      category: string,
      targetPage?: number,
      skipLoadingGuard?: boolean,
    ) => {
      const currentPagination = categoryPagination[category];
      if (!currentPagination) {
        return;
      }

      const pageToLoad = targetPage || currentPagination.page;

      if (!skipLoadingGuard && currentPagination.loading) {
        return;
      }

      setCategoryPagination((prev) => ({
        ...prev,
        [category]: { ...prev[category], loading: true },
      }));

      try {
        const result = await loadCategoryData(
          category,
          pageToLoad,
          currentPagination.limit,
        );

        // Transform the response data
        const transformedNodes = result.data.map((node: any) => {
          if (node.id && node.label && node.properties) {
            return transformGraphNodeToComponent(node);
          } else {
            return transformGraphNodeToComponent({
              id:
                node.id ||
                `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              label: categoryToLabel(category),
              properties: node,
            });
          }
        });

        // Calculate total pages based on total count and limit
        const totalCount = result.total || 0;
        const totalPages = Math.ceil(totalCount / currentPagination.limit);

        // Store current page data separately for display
        setCurrentPageData((prev) => ({
          ...prev,
          [category]: transformedNodes,
        }));

        // Update pagination with correct hasMore logic and total pages
        setCategoryPagination((prev) => ({
          ...prev,
          [category]: {
            ...prev[category],
            page: pageToLoad,
            hasMore: pageToLoad < totalPages,
            totalPages: totalPages,
            loading: false,
          },
        }));
      } catch (error) {
        console.error(`Error loading ${category}:`, error);
        setCategoryPagination((prev) => ({
          ...prev,
          [category]: { ...prev[category], loading: false },
        }));
      }
    },
    [categoryPagination, loadCategoryData],
  );

  // Store the function in ref for use in useEffect
  useEffect(() => {
    loadCategoryDataRef.current = loadCategoryDataInternal;
  }, [loadCategoryDataInternal]);

  // Layer keys are snake_case (observability_monitoring); the human label uses
  // an ampersand ("Observability & Monitoring") to match the Document
  // Guidelines panel in ontology-manager.tsx and the reader's mental model.
  // Keep this map in sync with `displayCategoryToKey` below.
  const CATEGORY_DISPLAY_OVERRIDES: Record<string, string> = {
    observability_monitoring: "Observability & Monitoring",
  };

  const formatCategoryName = (c: string) =>
    CATEGORY_DISPLAY_OVERRIDES[c] ??
    c.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  // Inverse of formatCategoryName — must handle the " & " that the override
  // introduces, or "Observability & Monitoring" round-trips to a bogus key
  // like "observability_&_monitoring".
  const displayCategoryToKey = (display: string): string =>
    display.toLowerCase().replace(/ & /g, "_").replace(/ /g, "_");

  const pluralizeCategoryName = (c: string) => {
    const formatted = formatCategoryName(c).toLowerCase();
    // Handle special plural cases
    if (formatted.endsWith("y")) {
      return formatted.slice(0, -1) + "ies";
    }
    return formatted + "s";
  };

  // Initialize pagination with totalPages
  useEffect(() => {
    setCategoryPagination((prev) => {
      const next = { ...prev };
      allPossibleCategories.forEach((cat) => {
        if (!next[cat]) {
          // Set limit to 5 for data_lake, 10 for others
          const limit = cat === "data_lake" ? 5 : 10;
          next[cat] = {
            page: 1,
            limit: limit,
            hasMore: false,
            loading: false,
            totalPages: 0,
          };
        }
      });
      return next;
    });
  }, [allPossibleCategories]);

  // Expand personalizedUx by default and load its data (only when graph data is available)
  useEffect(() => {
    if (
      allPossibleCategories.length > 0 &&
      !expandedCategory &&
      !isLoadingGraph &&
      graphData
    ) {
      const firstCategory = "user_experience";
      setExpandedCategory(firstCategory);
      setTimeout(() => {
        if (loadCategoryDataRef.current) {
          loadCategoryDataRef.current(firstCategory, 1);
        }
      }, 100);
    }
  }, [allPossibleCategories, expandedCategory, isLoadingGraph, graphData]);

  // Listen for architecture data updates to refresh category display
  useEffect(() => {
    const handleArchitectureDataUpdate = (event: CustomEvent) => {
      const { category } = event.detail;

      // Clear current page data
      setCurrentPageData((prev) => ({
        ...prev,
        [category]: [],
      }));

      // Reset pagination to first page
      setCategoryPagination((prev) => ({
        ...prev,
        [category]: { ...prev[category], page: 1 },
      }));

      // Reload the category data immediately
      if (loadCategoryDataRef.current) {
        loadCategoryDataRef.current(category, 1);
      }
    };

    window.addEventListener(
      "architecture-data-updated",
      handleArchitectureDataUpdate as EventListener,
    );

    return () => {
      window.removeEventListener(
        "architecture-data-updated",
        handleArchitectureDataUpdate as EventListener,
      );
    };
  }, []);

  // Collapse any expanded subsection when a snapshot is restored, so stale
  // category lists aren't shown. Fresh data will be fetched on next click.
  useEffect(() => {
    const handleSnapshotRestored = () => {
      setExpandedCategory(null);
      setSelectedArchNode(null);
      setSelectedTableIndex(null);
      setCurrentPageData({});
    };
    window.addEventListener("snapshot-restored", handleSnapshotRestored);
    return () => {
      window.removeEventListener("snapshot-restored", handleSnapshotRestored);
    };
  }, []);

  // Process graph data from parent to get category counts
  useEffect(() => {
    if (graphData?.architectural_specs) {
      // Store the counts for each category (normalize: arrays → length, numbers → as-is)
      const normalizedCounts: Record<string, number> = {};
      Object.entries(graphData.architectural_specs).forEach(([key, val]) => {
        if (typeof val === "number") normalizedCounts[key] = val;
        else if (Array.isArray(val)) normalizedCounts[key] = val.length;
        else normalizedCounts[key] = 0;
      });
      setCategoryCounts(normalizedCounts);

      // Update pagination totalPages when we get total counts
      setCategoryPagination((prev) => {
        const updated = { ...prev };
        Object.entries(normalizedCounts).forEach(([category, count]) => {
          if (updated[category]) {
            const totalPages = Math.ceil(count / updated[category].limit);
            updated[category] = {
              ...updated[category],
              totalPages: totalPages,
              hasMore: updated[category].page < totalPages,
            };
          }
        });
        return updated;
      });

      // Enable the architecture tab since we have data
      if (setTabs) {
        setTabs((prev) =>
          prev.map((tab) =>
            tab.name === "Architecture" ? { ...tab, enabled: true } : tab,
          ),
        );
      }

      // Force reload the currently expanded category's data so the displayed
      // node list reflects the new graphData (e.g. after a snapshot restore
      // invalidates the neo4j-graph query). loadCategoryDataInternal calls
      // apiFetch directly rather than going through React Query, so without
      // this it would keep showing pre-restore items for non-user_experience
      // categories.
      // Reload expanded category if graphData changed (e.g. snapshot restore).
      // Read from ref to avoid adding expandedCategory as a dependency,
      // which would cause unnecessary re-runs on every expand/collapse.
      const currentExpanded = expandedCategoryRef.current;
      if (currentExpanded && loadCategoryDataRef.current) {
        loadCategoryDataRef.current(currentExpanded, 1, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphData, setTabs]);

  const handleAccordionChange = (value: string) => {
    const newExpanded = value || null;
    setExpandedCategory(newExpanded);
    setSelectedArchNode(null);
    setSelectedTableIndex(null);

    if (newExpanded) {
      setTablesPagination({ page: 1, limit: 5 });
      setCategoryPagination((prev) => ({
        ...prev,
        [newExpanded]: {
          ...(prev[newExpanded] || {
            page: 1,
            limit: 10,
            hasMore: false,
            totalPages: 0,
          }),
          loading: true,
        },
      }));
      setCurrentPageData((prev) => ({ ...prev, [newExpanded]: [] }));
      loadCategoryDataInternal(newExpanded, 1, true);
    }
  };

  // Handler for selecting a table (mirrors persona → tasks click pattern)
  const handleSelectTable = useCallback((tableIndex: number) => {
    setSelectedTableIndex((prev) => (prev === tableIndex ? null : tableIndex));
    setEditingColumn(null);
    setEditedColumnData(null);
    setColumnsPagination({ page: 1, limit: 5 });
    setIndexesPagination({ page: 1, limit: 5 });
    setConstraintsPagination({ page: 1, limit: 5 });
  }, []);

  // Get tables for the CURRENT PAGE of the selected data lake node. When the
  // server-paginated response is available, prefer it (indices below are page-
  // relative — `startIndex` from getPaginatedTables converts to absolute keys).
  const getNodeTables = useCallback((): Table[] => {
    if (!selectedArchNode || selectedArchNode.group !== "data_lake") return [];

    const pageItems = dbOntologyTablesPage?.items;
    if (Array.isArray(pageItems) && pageItems.length > 0) {
      return pageItems as Table[];
    }

    const tables =
      selectedArchNode?.metadata?.blueprint?.tables ||
      selectedArchNode?.tables ||
      [];
    return Array.isArray(tables) ? tables : [];
  }, [selectedArchNode, dbOntologyTablesPage]);

  // Resolve the currently selected table (memoized so we can drive a column fetch off it)
  const selectedTableForColumns = useMemo<Table | null>(() => {
    if (selectedTableIndex === null) return null;
    const all = getNodeTables();
    return all[selectedTableIndex] ?? null;
  }, [selectedTableIndex, getNodeTables]);

  // Fetch columns scoped to the selected table via the DDLColumn ontology endpoint.
  // DDLColumnGraph stores `tableId` (parent DDLTable node id), so that's the only
  // reliable filter — columns don't carry a tableName field.
  const selectedTableNodeId = selectedTableForColumns?.id;
  const {
    data: selectedTableColumns,
    isFetching: isFetchingSelectedTableColumns,
  } = useGetDbOntologyColumns(
    dataLakeNodeId,
    isSelectedDataLake && !!selectedTableNodeId,
    { projectUuid, tableId: selectedTableNodeId, limit: 1000 },
  );

  // Indexes & Constraints are also scoped to the selected table (same pattern).
  const { data: dbOntologyIndexes, isFetching: isFetchingDbOntologyIndexes } =
    useGetDbOntologyIndexes(
      dataLakeNodeId,
      isSelectedDataLake && !!selectedTableNodeId,
      { projectUuid, tableId: selectedTableNodeId, limit: 1000 },
    );
  const {
    data: dbOntologyConstraints,
    isFetching: isFetchingDbOntologyConstraints,
    refetch: refetchDbOntologyConstraints,
  } = useGetDbOntologyConstraints(
    dataLakeNodeId,
    isSelectedDataLake && !!selectedTableNodeId,
    { projectUuid, tableId: selectedTableNodeId, limit: 1000 },
  );

  // Server-paginated: hook returns the current page directly, so `tables` is
  // already the slice we render. `total` comes from the API envelope so the
  // footer / page count reflects the full row count (up to backend cap).
  const getPaginatedTables = useCallback(() => {
    const pageTables = getNodeTables();
    const total = dbOntologyTablesPage?.total ?? pageTables.length;
    const totalPages = Math.max(1, Math.ceil(total / tablesPagination.limit));
    const startIndex = (tablesPagination.page - 1) * tablesPagination.limit;
    return {
      tables: pageTables,
      total,
      totalPages,
      startIndex,
    };
  }, [getNodeTables, dbOntologyTablesPage, tablesPagination]);

  // Handler to start editing a table
  const handleEditTable = useCallback((tableIndex: number, table: Table) => {
    setEditingTable(tableIndex);
    setEditedTableData({ ...table });
    // Ensure the table is selected when editing so the editor is visible
    setSelectedTableIndex(tableIndex);
  }, []);

  // Handler to save edited table
  const handleSaveTable = useCallback(
    async (tableIndex: number) => {
      if (!selectedArchNode || !editedTableData) return;

      try {
        if (editedTableData.id) {
          // Update the DDLTable node directly through the ontology API.
          // Backend cascades the rename onto child columns/constraints/indexes.
          await updateDbOntologyNode.mutateAsync({
            label: "DDLTable",
            projectUuid,
            id: editedTableData.id,
            dataLakeId: selectedArchNode.id,
            data: {
              name: editedTableData.name,
              ...(editedTableData.description !== undefined
                ? { comment: editedTableData.description }
                : {}),
            },
          });
        } else {
          // Legacy path: persist via the data-lake node blueprint.
          const allTables = getNodeTables();
          const updatedTables = [...allTables];
          updatedTables[tableIndex] = editedTableData;
          const label = categoryToLabel("data_lake");
          await updateNodeMutation.mutateAsync({
            nodeId: selectedArchNode.id || "",
            label: label,
            data: {
              ...selectedArchNode.metadata?.blueprint,
              tables: updatedTables,
            },
          });
        }

        setEditingTable(null);
        setEditedTableData(null);

        // Reload the data lake to show updates
        setTimeout(() => {
          loadCategoryDataInternal(
            "data_lake",
            categoryPagination["data_lake"]?.page || 1,
          );
        }, 300);
      } catch (error) {
        console.error("Error saving table:", error);
        toast.error("Failed to save table");
      }
    },
    [
      selectedArchNode,
      editedTableData,
      getNodeTables,
      updateNodeMutation,
      updateDbOntologyNode,
      loadCategoryDataInternal,
      categoryPagination,
    ],
  );

  // Handler to delete a table
  const handleDeleteTable = useCallback(
    async (tableIndex: number) => {
      if (!selectedArchNode) return;

      try {
        const allTables = getNodeTables();
        const tableToDelete = allTables[tableIndex];

        if (tableToDelete?.id) {
          // Backend cascade-delete handles the table + columns/constraints/indexes.
          // No need to also rewrite blueprint.tables on the node.
          await deleteDbOntologyTable.mutateAsync({
            projectUuid,
            tableId: tableToDelete.id,
            dataLakeId: selectedArchNode.id,
          });
        } else {
          // Legacy path: table only lives on the node blueprint, persist removal there.
          const updatedTables = allTables.filter(
            (_, idx) => idx !== tableIndex,
          );
          const label = categoryToLabel("data_lake");
          await updateNodeMutation.mutateAsync({
            nodeId: selectedArchNode.id || "",
            label: label,
            data: {
              ...selectedArchNode.metadata?.blueprint,
              tables: updatedTables,
            },
          });
        }

        // Clear selection if this was the selected table
        setSelectedTableIndex((prev) => (prev === tableIndex ? null : prev));

        toast.success("Table deleted successfully");

        // Reload the data lake to show updates
        setTimeout(() => {
          loadCategoryDataInternal(
            "data_lake",
            categoryPagination["data_lake"]?.page || 1,
          );
        }, 300);
      } catch (error) {
        console.error("Error deleting table:", error);
        toast.error("Failed to delete table");
      }
    },
    [
      selectedArchNode,
      getNodeTables,
      updateNodeMutation,
      deleteDbOntologyTable,
      loadCategoryDataInternal,
      categoryPagination,
    ],
  );

  // Handler to start editing a column
  const handleEditColumn = useCallback(
    (tableIndex: number, columnIndex: number, column: Column) => {
      setEditingColumn({ tableIndex, columnIndex });
      setEditedColumnData({ ...column });
    },
    [],
  );

  // Handler to save edited column
  const handleSaveColumn = useCallback(
    async (tableIndex: number, columnIndex: number) => {
      if (!selectedArchNode || !editedColumnData) return;

    // A primary key column is always NOT NULL. Stop here with the reason instead
    // of sending the save: when the column is already a PK the backend rejects
    // it, and when PK is being switched on in the same save the nullable write
    // lands first and is then silently reset to false by the new constraint.
    if (editedColumnData.primaryKey && editedColumnData.nullable) {
      toast.error("Primary key columns cannot be nullable. Turn off Nullable, or remove the Primary Key.");
      return;
    }

      try {
        if (editedColumnData.id) {
          // Parse "VARCHAR(255)" → { dataType: "VARCHAR", length: 255 }
          const rawType = String(editedColumnData.type ?? "").trim();
          const match = rawType.match(/^([^()\s]+)\s*\((\d+)\)\s*$/);
          const dataType = match ? match[1] : rawType;
          const length = match ? Number(match[2]) : undefined;

          await updateDbOntologyNode.mutateAsync({
            label: "DDLColumn",
            projectUuid,
            id: editedColumnData.id,
            dataLakeId: selectedArchNode.id,
            data: {
              ...(dataType ? { dataType } : {}),
              ...(length !== undefined ? { length } : {}),
              ...(editedColumnData.nullable !== undefined
                ? { nullable: editedColumnData.nullable }
                : {}),
              ...(editedColumnData.description !== undefined
                ? { comment: editedColumnData.description }
                : {}),
            },
          });

          // HUBS-160: the inline "Primary Key" switch is NOT a writable DDLColumn
          // property — PK membership lives on a PRIMARY_KEY DDLConstraint, and the
          // column's isPrimaryKey flag is derived from that constraint on the
          // backend. Persisting the toggle therefore has to add/remove this column
          // to/from the table's PRIMARY_KEY constraint; otherwise the switch
          // silently no-ops on save (the original bug).
          const columnName = editedColumnData.name;
          const tableNodeId = selectedTableForColumns?.id;
          const dataLakeNodeIdForPk = selectedArchNode.id;
          // Read the table's CURRENT primary key fresh from the backend rather
          // than the (up to 60s) cached list. Reassigning a PK is two separate
          // per-column saves — remove from column A, then add to column B — so
          // this save must observe the result of the previous one. Acting on the
          // stale cache targets a deleted/outdated constraint and the change is
          // silently lost.
          let cons = Array.isArray(dbOntologyConstraints)
            ? dbOntologyConstraints
            : [];
          try {
            const fresh = await refetchDbOntologyConstraints();
            if (Array.isArray(fresh.data)) cons = fresh.data;
          } catch {
            // fall back to the cached list if the refetch fails
          }
          const pkConstraint = cons.find(
            (c) => c.constraintType === "PRIMARY_KEY",
          );
          const pkColumns = pkConstraint?.columns ?? [];
          const wasPrimaryKey = pkColumns.includes(columnName);
          const wantPrimaryKey = !!editedColumnData.primaryKey;

          if (
            columnName &&
            tableNodeId &&
            dataLakeNodeIdForPk &&
            wantPrimaryKey !== wasPrimaryKey
          ) {
            if (wantPrimaryKey) {
              // Add this column to the table's primary key (extend an existing
              // PRIMARY_KEY constraint, or create one if the table has none).
              if (pkConstraint?.id) {
                await updateDbOntologyNode.mutateAsync({
                  label: "DDLConstraint",
                  projectUuid,
                  id: pkConstraint.id,
                  dataLakeId: dataLakeNodeIdForPk,
                  data: { columns: [...pkColumns, columnName] },
                });
              } else {
                await createDbOntologyConstraint.mutateAsync({
                  projectUuid,
                  dataLakeId: dataLakeNodeIdForPk,
                  tableId: tableNodeId,
                  name: null,
                  constraintType: "PRIMARY_KEY",
                  columns: [columnName],
                });
              }
            } else if (pkConstraint?.id) {
              // Remove this column from the primary key. Drop the whole
              // constraint when it would otherwise be left with no columns.
              const remaining = pkColumns.filter((c) => c !== columnName);
              if (remaining.length === 0) {
                await deleteDbOntologyConstraint.mutateAsync({
                  constraintId: pkConstraint.id,
                  projectUuid,
                  dataLakeId: dataLakeNodeIdForPk,
                });
              } else {
                await updateDbOntologyNode.mutateAsync({
                  label: "DDLConstraint",
                  projectUuid,
                  id: pkConstraint.id,
                  dataLakeId: dataLakeNodeIdForPk,
                  data: { columns: remaining },
                });
              }
            }
          }
        } else {
          // Legacy path: persist via the data-lake node blueprint.
          const allTables = getNodeTables();
          const updatedTables = [...allTables];
          updatedTables[tableIndex].columns[columnIndex] = editedColumnData;
          const label = categoryToLabel("data_lake");
          await updateNodeMutation.mutateAsync({
            nodeId: selectedArchNode.id || "",
            label: label,
            data: {
              ...selectedArchNode.metadata?.blueprint,
              tables: updatedTables,
            },
          });
        }

        setEditingColumn(null);
        setEditedColumnData(null);

        // Reload the data lake to show updates
        setTimeout(() => {
          loadCategoryDataInternal(
            "data_lake",
            categoryPagination["data_lake"]?.page || 1,
          );
        }, 300);
      } catch (error) {
        console.error("Error saving column:", error);
        // The DDL ontology mutations (column update, constraint create/update/
        // delete) already toast the backend's own error message — e.g. "Column
        // 'x' is part of the table's primary key and cannot be nullable". A second
        // generic toast here used to bury it, so only add one for the legacy path.
        if (!editedColumnData.id) {
          toast.error("Failed to save column");
        }
      }
    },
    [
      selectedArchNode,
      editedColumnData,
      getNodeTables,
      updateNodeMutation,
      updateDbOntologyNode,
      createDbOntologyConstraint,
      deleteDbOntologyConstraint,
      dbOntologyConstraints,
      refetchDbOntologyConstraints,
      selectedTableForColumns,
      projectUuid,
      loadCategoryDataInternal,
      categoryPagination,
    ],
  );

  // Handler to delete a column. `column` is the actual displayed column object —
  // we need it directly because the inline `table.columns` array is empty when
  // columns come from the per-table DDLColumn fetch instead of the blueprint.
  const handleDeleteColumn = useCallback(
    async (tableIndex: number, columnIndex: number, column: Column) => {
      if (!selectedArchNode) return;

      try {
        if (column?.id) {
          // Modern path: delete the DDLColumn node directly via the ontology API
          await deleteDbOntologyColumn.mutateAsync({
            projectUuid,
            columnId: column.id,
            dataLakeId: selectedArchNode.id,
          });
        } else {
          // Legacy path: rewrite the data-lake blueprint with the column removed
          const allTables = getNodeTables();
          const updatedTables = [...allTables];
          if (Array.isArray(updatedTables[tableIndex]?.columns)) {
            updatedTables[tableIndex].columns = updatedTables[
              tableIndex
            ].columns.filter((_, idx) => idx !== columnIndex);
          }

          const label = categoryToLabel("data_lake");
          await updateNodeMutation.mutateAsync({
            nodeId: selectedArchNode.id || "",
            label: label,
            data: {
              ...selectedArchNode.metadata?.blueprint,
              tables: updatedTables,
            },
          });
        }

        toast.success("Column deleted successfully");

        // Reload the data lake to show updates
        setTimeout(() => {
          loadCategoryDataInternal(
            "data_lake",
            categoryPagination["data_lake"]?.page || 1,
          );
        }, 300);
      } catch (error) {
        console.error("Error deleting column:", error);
        toast.error("Failed to delete column");
      }
    },
    [
      selectedArchNode,
      getNodeTables,
      updateNodeMutation,
      deleteDbOntologyColumn,
      loadCategoryDataInternal,
      categoryPagination,
    ],
  );

  // Node dialog handlers
  const handleOpenNodeDialog = (
    category: string,
    node: ArchitectureComponent | null = null,
  ) => {
    if (isViewer) {
      toast.error("You have view-only access to this project");
      return;
    }
    setCurrentCategory(formatCategoryName(category));
    setCurrentNode(node);
    setNodeDialogOpen(true);
  };

  const handleCloseNodeDialog = () => {
    setNodeDialogOpen(false);
    setCurrentNode(null);
    setCurrentCategory("");
  };

  const handleNodeSubmit = async (data: any, ingestFiles: File[] = []) => {
    try {
      const categoryKey = displayCategoryToKey(currentCategory);
      const label = categoryToLabel(categoryKey);

      // Stream-ingest all picked files against the given data-lake id in a
      // single batched POST. Bundling matters for ES: settings + mapping for
      // the same index need to land in the same generator call so the parser
      // can merge them into one record. Splitting them into separate POSTs
      // races on the graph MERGE and can lose settings.
      const ingestFilesBatched = async (dataLakeId: string) => {
        if (ingestFiles.length === 0) return;
        try {
          await streamIngestSql.mutateAsync({
            files: ingestFiles,
            projectUuid,
            dataLakeId,
          });
        } catch {
          // toast handled in mutation
        }
      };

      if (currentNode) {
        // Update existing node — ingest uploads are not offered during update,
        // so any `ingestFiles` passed in are ignored here.
        await updateNodeMutation.mutateAsync({
          nodeId: currentNode.id || "",
          label: label,
          data: data,
        });

        // Reload current page after update
        const currentPage = categoryPagination[categoryKey]?.page || 1;
        setTimeout(() => {
          loadCategoryDataInternal(categoryKey, currentPage);
        }, 300);
      } else {
        // Create new node — capture the response so we can grab the new node id
        const createResult: any = await createNodeMutation.mutateAsync({
          label: label,
          data: data,
        });

        // For a new data-lake create, if the user attached file(s), stream-ingest
        // each one against the freshly-created node id. The mutation toasts on
        // success/error.
        if (ingestFiles.length > 0 && categoryKey === "data_lake") {
          const newNodeId =
            createResult?.id ||
            createResult?.node?.id ||
            createResult?.data?.id ||
            createResult?.result?.id;
          if (newNodeId) {
            await ingestFilesBatched(String(newNodeId));
          } else {
            toast.error(
              "Data lake created, but couldn't find its id to upload the file(s).",
            );
          }
        }

        // Reload first page after create
        setTimeout(() => {
          loadCategoryDataInternal(categoryKey, 1);
        }, 300);
      }

      handleCloseNodeDialog();
    } catch (error) {
      if (error instanceof Error && error.message) {
        toast.error(error.message);
      } else {
        console.error("Error saving node:", error);
        toast.error(
          currentNode
            ? "Failed to update component"
            : "Failed to create component1",
        );
      }
    }
  };

  const handleDeleteNode = (node: ArchitectureComponent, category: string) => {
    if (isViewer) {
      toast.error("You have view-only access to this project");
      return;
    }
    setNodeToDelete({ node, category });
    setConfirmOpen(true);
  };

  const handleRegenerateEmbeddings = () => {
    if (isViewer) {
      toast.error("You have view-only access to this project");
      return;
    }
    if (!projectUuid) {
      toast.error("Project UUID not found");
      return;
    }
    setEmbeddingConfirmOpen(true);
  };

  const handleConfirmRegenerateEmbeddings = () => {
    if (projectUuid) {
      regenerateArchitectureEmbeddings({ projectUuid, regenerate: true });
    }
    setEmbeddingConfirmOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!nodeToDelete) return;

    try {
      const label = categoryToLabel(
        displayCategoryToKey(nodeToDelete.category),
      );

      await deleteNodeMutation.mutateAsync({
        label: label,
        nodeId: nodeToDelete.node.id || "",
      });

      // Reload current page after delete
      const category = displayCategoryToKey(nodeToDelete.category);
      const currentPage = categoryPagination[category]?.page || 1;
      setTimeout(() => {
        loadCategoryDataInternal(category, currentPage);
      }, 300);

      setConfirmOpen(false);
      setNodeToDelete(null);
    } catch (error) {
      console.error("Error deleting node:", error);
      toast.error("Failed to delete component");
    }
  };

  const handlePopupSave = useCallback(
    async (updated: ArchitectureComponent) => {
      await handleUpdateSingleComponent(updated);
      setPopupOpen(false);

      // Reload the category data to show updated information
      if (updated.group) {
        // Reload current page
        const currentPage = categoryPagination[updated.group!]?.page || 1;
        setTimeout(() => {
          loadCategoryDataInternal(updated.group!, currentPage);
        }, 300);
      }
    },
    [handleUpdateSingleComponent, loadCategoryDataInternal, categoryPagination],
  );

  // Pagination navigation handlers
  const handleNextPage = useCallback(
    (category: string) => {
      const pagination = categoryPagination[category];
      if (!pagination || pagination.loading || !pagination.hasMore) return;

      const nextPage = pagination.page + 1;
      loadCategoryDataInternal(category, nextPage);
    },
    [categoryPagination, loadCategoryDataInternal],
  );

  const handlePreviousPage = useCallback(
    (category: string) => {
      const pagination = categoryPagination[category];
      if (!pagination || pagination.loading || pagination.page <= 1) return;

      const prevPage = pagination.page - 1;
      loadCategoryDataInternal(category, prevPage);
    },
    [categoryPagination, loadCategoryDataInternal],
  );

  // Use current page data for display
  const getCategoryComponents = (c: string) => currentPageData[c] || [];

  const getCategoryCount = (c: string): number => {
    const val = categoryCounts[c];
    if (typeof val === "number") return val;
    //@ts-ignore
    if (Array.isArray(val)) return val.length;
    return 0;
  };
  console.log(expandedCategory);
  return (
    <div
      className={`flex-col flex overflow-auto flex-grow px-0 pt-3 ${CUSTOM_SCROLLBAR}`}
    >
      {isLoadingGraph ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-white" />
          <span className="ml-2 text-white">
            Loading architecture specifications...
          </span>
        </div>
      ) : (
        <>
          {/* Architecture Header - Always show */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-xl font-semibold text-white mb-1">
                Breeze Blueprint Architecture
              </h2>
              <p className="text-xs text-muted">
                Layered architecture components organized by functional domains.
                Click + to add components manually.
              </p>
            </div>
            <div className="flex gap-2">
              {isAdmin && !isViewer && (
                <CustomTooltip text="Re-generate Embeddings" side="bottom">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleRegenerateEmbeddings}
                    className="border-surface-border text-white hover:bg-surface-hover hover:border-primary"
                  >
                    <Sparkles className="h-4 w-4" />
                  </Button>
                </CustomTooltip>
              )}
              {onVisualizeArchitectureClick &&
                (() => {
                  // BREEZEAI-548 Scenario 10: keep this button disabled until the
                  // user has actually added or generated at least one component —
                  // running Visualize against an empty blueprint produced an
                  // empty diagram and was flagged as confusing.
                  const totalComponents = allPossibleCategories.reduce(
                    (sum, c) => sum + getCategoryCount(c),
                    0,
                  );
                  const canVisualize = totalComponents > 0;
                  return (
                    // BREEZEAI-1013: the accessible name must state what the
                    // button does, so it is pinned to "Visualize Architecture"
                    // instead of inheriting the tooltip text — otherwise the
                    // disabled-state hint became the button's name and failed
                    // WCAG 2.1 SC 4.1.2. The hint is exposed as a description.
                    <CustomTooltip
                      text={
                        canVisualize
                          ? "Visualize Architecture"
                          : "Add or generate at least one component first"
                      }
                      side="bottom"
                    >
                      <Button
                        onClick={
                          canVisualize
                            ? onVisualizeArchitectureClick
                            : undefined
                        }
                        aria-disabled={!canVisualize}
                        aria-label="Visualize Architecture"
                        aria-describedby={
                          canVisualize ? undefined : VISUALIZE_HINT_ID
                        }
                        variant="outline"
                        size="icon"
                        className={`border-surface-border text-white hover:bg-surface-hover hover:border-primary ${
                          canVisualize ? "" : "opacity-50 cursor-not-allowed"
                        }`}
                      >
                        <Play className="h-4 w-4" />
                        {!canVisualize && (
                          <span id={VISUALIZE_HINT_ID} className="sr-only">
                            Add or generate at least one component first
                          </span>
                        )}
                      </Button>
                    </CustomTooltip>
                  );
                })()}
            </div>
          </div>

          {/* Architecture Categories - Always show so users can add components */}
          <Accordion
            type="single"
            collapsible
            defaultValue=""
            onValueChange={handleAccordionChange}
            className="space-y-2"
          >
            {allPossibleCategories.map((c) => {
              const comps = getCategoryComponents(c);
              const count = getCategoryCount(c);
              const pagination = categoryPagination[c] || {
                page: 1,
                limit: 10,
                hasMore: false,
                loading: false,
                totalPages: 0,
              };

              return (
                <Fragment key={c}>
                  <AccordionItem
                    value={c}
                    className="bg-surface-card rounded-lg border border-surface-border overflow-hidden border-b-0"
                  >
                    <AccordionTrigger className="flex justify-between items-center p-3 hover:bg-surface-hover transition-colors hover:no-underline [&>svg]:hidden">
                      <div className="flex items-center gap-2">
                        {formatCategoryName(c)}
                        {count > 0 && (
                          <Badge
                            variant="primary"
                            className="min-w-[20px] text-center"
                          >
                            {count}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {!isViewer && (
                          <CustomTooltip text={`Add ${formatCategoryName(c)}`}>
                            <Button
                              variant="outline"
                              size="icon"
                              className="bg-primary hover:bg-primary-dark"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenNodeDialog(c);
                              }}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </CustomTooltip>
                        )}
                      </div>
                    </AccordionTrigger>

                    <AccordionContent className="p-3 pt-0 space-y-2">
                      <div className="grid grid-cols-1 gap-2 text-left">
                        {pagination.loading && comps.length === 0 ? (
                          <div className="py-3 text-muted text-sm flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading {formatCategoryName(c).toLowerCase()}...
                          </div>
                        ) : comps.length > 0 ? (
                          comps.map((comp, idx) => (
                            <div
                              key={
                                comp.id ||
                                String(comp.metadata?.name || comp.name || idx)
                              }
                              className="relative group"
                            >
                              <PersonaCard
                                title={String(
                                  comp?.metadata?.blueprint?.name ||
                                    comp?.name ||
                                    "Unnamed",
                                )}
                                description={String(
                                  comp?.metadata?.blueprint?.description ||
                                    comp?.description ||
                                    "",
                                )}
                                onClick={() => {
                                  const isDeselecting =
                                    selectedArchNode?.id === comp.id;
                                  setSelectedArchNode(
                                    isDeselecting ? null : comp,
                                  );

                                  // Reset tables pagination and selection when selecting a different node
                                  if (c === "data_lake") {
                                    setTablesPagination({
                                      page: 1,
                                      limit: 5,
                                    });
                                    setColumnsPagination({
                                      page: 1,
                                      limit: 5,
                                    });
                                    setViewsPagination({ page: 1, limit: 5 });
                                    setProceduresPagination({
                                      page: 1,
                                      limit: 5,
                                    });
                                    setIndexesPagination({
                                      page: 1,
                                      limit: 5,
                                    });
                                    setConstraintsPagination({
                                      page: 1,
                                      limit: 5,
                                    });
                                    setSelectedTableIndex(null);
                                    setEditingTable(null);
                                    setEditedTableData(null);
                                    setEditingColumn(null);
                                    setEditedColumnData(null);
                                  }
                                }}
                                isActive={
                                  selectedArchNode?.id === comp.id ||
                                  selectedEntity === comp?.metadata?.name
                                }
                                headerSize={"text-lg"}
                                contentSize={"text-base"}
                              />
                              {!isViewer && (
                                <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <CustomTooltip text="Edit">
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      onClick={() =>
                                        handleOpenNodeDialog(c, comp)
                                      }
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                  </CustomTooltip>
                                  <CustomTooltip text="Delete">
                                    <Button
                                      variant="destructive"
                                      size="icon"
                                      onClick={() =>
                                        handleDeleteNode(
                                          comp,
                                          formatCategoryName(c),
                                        )
                                      }
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </CustomTooltip>
                                </div>
                              )}
                            </div>
                          ))
                        ) : count > 0 ? (
                          <div className="p-2 pl-0 text-info text-sm">
                            {count} {formatCategoryName(c).toLowerCase()}{" "}
                            available.
                            <button
                              onClick={() => loadCategoryDataInternal(c, 1)}
                              className="ml-2 underline hover:text-primary"
                            >
                              Load now
                            </button>
                          </div>
                        ) : (
                          <div className="py-3 text-muted text-sm">
                            No {formatCategoryName(c).toLowerCase()} configured.
                            Click "Add New" to add components.
                          </div>
                        )}
                      </div>

                      {/* Pagination controls - only show when total items exceed limit */}
                      {count > pagination.limit && (
                        <Pagination
                          currentItems={comps.length}
                          totalItems={count}
                          currentPage={pagination.page}
                          totalPages={pagination.totalPages}
                          isLoading={pagination.loading}
                          onPrevious={() => handlePreviousPage(c)}
                          onNext={() => handleNextPage(c)}
                          canGoPrevious={pagination.page > 1}
                          canGoNext={
                            pagination.hasMore &&
                            pagination.page < pagination.totalPages
                          }
                          itemName={pluralizeCategoryName(c)}
                          showPageNumbers={true}
                          mode="pages"
                        />
                      )}

                      {/* Citations for the selected component in this category */}
                      {selectedArchNode &&
                        selectedArchNode.group === c &&
                        archNodeCitations &&
                        archNodeCitations.length > 0 && (
                          <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-medium px-2 py-0.5 rounded bg-warning/20 text-warning uppercase tracking-wider">
                                {formatCategoryName(c)}
                              </span>
                              <span className="text-sm text-white truncate">
                                {String(
                                  selectedArchNode?.metadata?.blueprint?.name ||
                                    selectedArchNode?.name ||
                                    "Unnamed",
                                )}
                              </span>
                              <span className="text-xs text-muted">
                                — Citations
                              </span>
                            </div>
                            <CitationList
                              citations={archNodeCitations}
                              onCitationClick={handleNodeCitationClick}
                            />
                          </div>
                        )}

                      {/* Tables & Columns sections — DDL-only; hidden for Elastic and Non-Relational data lakes */}
                      {c === "data_lake" &&
                        expandedCategory === "data_lake" &&
                        selectedArchNode &&
                        selectedArchNode.group === "data_lake" &&
                        showDdlPanels &&
                        (() => {
                          const { tables, total, totalPages, startIndex } =
                            getPaginatedTables();
                          const allTables = getNodeTables();
                          const selectedTable =
                            selectedTableIndex !== null
                              ? allTables[selectedTableIndex]
                              : null;
                          const isEditingSelectedTable =
                            selectedTableIndex !== null &&
                            editingTable === selectedTableIndex;
                          const fetchedColumns: Column[] = Array.isArray(
                            selectedTableColumns,
                          )
                            ? (selectedTableColumns as Column[])
                            : [];
                          const allColumns: Column[] =
                            fetchedColumns.length > 0
                              ? fetchedColumns
                              : selectedTable?.columns || [];
                          const columnsTotal = allColumns.length;
                          const columnsTotalPages = Math.max(
                            1,
                            Math.ceil(columnsTotal / columnsPagination.limit),
                          );
                          const columnsStartIndex =
                            (columnsPagination.page - 1) *
                            columnsPagination.limit;
                          const displayColumns: Column[] = allColumns.slice(
                            columnsStartIndex,
                            columnsStartIndex + columnsPagination.limit,
                          );

                          return (
                            <>
                              {/* Ingest status — surfaces the hosted generator's
                                  async parse so a user who uploaded a large
                                  .sql file isn't staring at "0 of 0" with no
                                  signal that anything is in flight. */}
                              {ingestionStatus === "ingesting" && (
                                <div
                                  role="status"
                                  aria-live="polite"
                                  className="bg-surface-card rounded-lg p-3 border border-primary/40 flex items-start gap-3"
                                >
                                  <Loader2 className="w-4 h-4 animate-spin text-primary mt-0.5 shrink-0" />
                                  <div className="flex-1">
                                    <p className="text-xs text-white font-semibold">
                                      Analyzing uploaded SQL — this may take a few minutes.
                                    </p>
                                    <p className="text-[11px] text-muted mt-0.5">
                                      Tables, views and procedures will appear here as soon as parsing finishes. You can leave this panel open or come back later.
                                    </p>
                                  </div>
                                </div>
                              )}
                              {ingestionStatus === "error" && (
                                <div
                                  role="alert"
                                  className="bg-surface-card rounded-lg p-3 border border-destructive/40 flex items-start gap-3"
                                >
                                  <AlertCircle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
                                  <div className="flex-1">
                                    <p className="text-xs text-white font-semibold">
                                      SQL analysis failed.
                                    </p>
                                    <p className="text-[11px] text-muted mt-0.5 break-words">
                                      {ingestionError ||
                                        "The generator could not parse the uploaded file. Try re-uploading, or check the file for syntax errors."}
                                    </p>
                                  </div>
                                </div>
                              )}
                              {/* Tables Section */}
                              <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-3">
                                    <h4 className="text-lg text-white font-semibold">
                                      Tables
                                    </h4>
                                    <span className="text-sm text-muted bg-surface-dark px-1.5 py-0.5 rounded">
                                      {`${tables.length} of ${total}`}
                                    </span>
                                    <span className="text-sm text-white truncate">
                                      —{" "}
                                      {String(
                                        selectedArchNode?.metadata?.blueprint
                                          ?.name ||
                                          selectedArchNode?.name ||
                                          "Unnamed",
                                      )}
                                    </span>
                                    {isFetchingDbOntology && (
                                      <Loader2 className="w-4 h-4 animate-spin text-muted" />
                                    )}
                                  </div>
                                  {!isViewer && (
                                    <CustomTooltip text="Add Table">
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        className="bg-primary hover:bg-primary-dark"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setCreateTableOpen(true);
                                        }}
                                      >
                                        <Plus className="h-4 w-4" />
                                      </Button>
                                    </CustomTooltip>
                                  )}
                                </div>

                                {tables.length === 0 ? (
                                  isFetchingDbOntology ? (
                                    <div className="text-sm text-muted text-center py-3 flex items-center justify-center gap-2">
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                      Loading tables...
                                    </div>
                                  ) : (
                                    <div className="text-xs text-muted text-center py-3">
                                      No tables defined. Click + to add one.
                                    </div>
                                  )
                                ) : (
                                  <div className="grid grid-cols-1 gap-2">
                                    {tables.map((table, paginatedIdx) => {
                                      // Server-side pagination returns the current
                                      // page's items; use the page-local index so
                                      // selection / delete lookups line up with
                                      // getNodeTables().
                                      const tableIndex = paginatedIdx;
                                      const displayIndex =
                                        startIndex + paginatedIdx;
                                      const isSelected =
                                        selectedTableIndex === tableIndex;
                                      return (
                                        <div
                                          key={tableIndex}
                                          className="relative group flex items-center justify-between"
                                        >
                                          <button
                                            type="button"
                                            onClick={() =>
                                              handleSelectTable(tableIndex)
                                            }
                                            className={`flex-1 text-left px-3 py-2.5 rounded border transition-colors ${
                                              isSelected
                                                ? "border-primary bg-primary/10 text-white"
                                                : "border-surface-border bg-surface-active hover:bg-surface-hover text-white"
                                            }`}
                                          >
                                            <div className="flex items-center gap-2">
                                              <span className="text-base font-medium">
                                                {table.name ||
                                                  `Table ${displayIndex + 1}`}
                                              </span>
                                              <span className="text-xs text-muted">
                                                (
                                                {typeof table.columnCount ===
                                                "number"
                                                  ? table.columnCount
                                                  : table.columns?.length ||
                                                    0}{" "}
                                                columns)
                                              </span>
                                            </div>
                                          </button>
                                          {!isViewer ? (
                                            <div className="absolute right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                              <CustomTooltip text="Edit Table">
                                                <Button
                                                  variant="outline"
                                                  size="icon"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditTable(
                                                      tableIndex,
                                                      table,
                                                    );
                                                  }}
                                                >
                                                  <Pencil className="h-4 w-4" />
                                                </Button>
                                              </CustomTooltip>
                                              <CustomTooltip text="Delete Table">
                                                <Button
                                                  variant="destructive"
                                                  size="icon"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setPendingDataLakeDelete({
                                                      type: "Table",
                                                      name:
                                                        table.name ||
                                                        `Table ${displayIndex + 1}`,
                                                      execute: () =>
                                                        handleDeleteTable(
                                                          tableIndex,
                                                        ),
                                                    });
                                                  }}
                                                >
                                                  <Trash2 className="h-4 w-4" />
                                                </Button>
                                              </CustomTooltip>
                                            </div>
                                          ) : (
                                            <div className="absolute right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                              <CustomTooltip text="View Table">
                                                <Button
                                                  variant="outline"
                                                  size="icon"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSelectTable(
                                                      tableIndex,
                                                    );
                                                  }}
                                                >
                                                  <Eye className="h-4 w-4" />
                                                </Button>
                                              </CustomTooltip>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}

                                {/* Tables Pagination */}
                                {total > tablesPagination.limit && (
                                  <div className="flex items-center justify-between px-2 py-2 mt-3 bg-surface-active rounded border border-surface-border">
                                    <span className="text-xs text-info">
                                      Page {tablesPagination.page} of{" "}
                                      {totalPages} ({tables.length} tables this
                                      page) • Total: {total} tables
                                    </span>
                                    <div className="flex items-center gap-2">
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          setTablesPagination((prev) => ({
                                            ...prev,
                                            page: prev.page - 1,
                                          }));
                                          // Selection is page-local (see Tables map) — clear on nav.
                                          setSelectedTableIndex(null);
                                        }}
                                        disabled={tablesPagination.page === 1}
                                        className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
                                      >
                                        <ChevronLeft className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          setTablesPagination((prev) => ({
                                            ...prev,
                                            page: prev.page + 1,
                                          }));
                                          setSelectedTableIndex(null);
                                        }}
                                        disabled={
                                          tablesPagination.page === totalPages
                                        }
                                        className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
                                      >
                                        <ChevronRight className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Columns Section — separate sibling card, only when a table is selected */}
                              {selectedTable && (
                                <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-3">
                                      <h4 className="text-lg text-white font-semibold">
                                        Columns
                                      </h4>
                                      <span className="text-xs text-muted bg-surface-dark px-1.5 py-0.5 rounded">
                                        {`${displayColumns.length} of ${columnsTotal}`}
                                      </span>
                                      <span className="text-sm text-white truncate">
                                        —{" "}
                                        {selectedTable.name ||
                                          `Table ${selectedTableIndex! + 1}`}
                                      </span>
                                      {isFetchingSelectedTableColumns && (
                                        <Loader2 className="w-4 h-4 animate-spin text-muted" />
                                      )}
                                    </div>
                                    {!isViewer && (
                                      <div className="flex gap-2">
                                        {!isEditingSelectedTable && (
                                          <CustomTooltip
                                            text="Edit Table Details"
                                            side="top"
                                          >
                                            <Button
                                              onClick={() =>
                                                handleEditTable(
                                                  selectedTableIndex!,
                                                  selectedTable,
                                                )
                                              }
                                              variant="outline"
                                              size="icon"
                                              className="bg-primary hover:bg-primary-dark"
                                            >
                                              <Pencil className="h-4 w-4" />
                                            </Button>
                                          </CustomTooltip>
                                        )}
                                        <CustomTooltip
                                          text="Add Column"
                                          side="top"
                                        >
                                          <Button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (!selectedTable?.id) {
                                                toast.error(
                                                  "Save the table first before adding columns",
                                                );
                                                return;
                                              }
                                              setCreateColumnOpen(true);
                                            }}
                                            variant="outline"
                                            size="icon"
                                            className="bg-primary hover:bg-primary-dark"
                                          >
                                            <Plus className="h-4 w-4" />
                                          </Button>
                                        </CustomTooltip>
                                      </div>
                                    )}
                                  </div>

                                  {isEditingSelectedTable && (
                                    <div className="space-y-3 mb-4 p-3 bg-surface-active rounded border border-surface-border">
                                      <div className="space-y-1.5">
                                        <label className="text-xs font-medium text-muted">
                                          Table Name
                                        </label>
                                        <Input
                                          value={editedTableData?.name || ""}
                                          onChange={(e) =>
                                            setEditedTableData((prev) =>
                                              prev
                                                ? {
                                                    ...prev,
                                                    name: e.target.value,
                                                  }
                                                : null,
                                            )
                                          }
                                          placeholder="Enter table name"
                                          className="bg-surface text-white text-sm h-7"
                                        />
                                      </div>
                                      <div className="space-y-1.5">
                                        <label className="text-xs font-medium text-muted">
                                          Description
                                        </label>
                                        <Textarea
                                          value={
                                            editedTableData?.description || ""
                                          }
                                          onChange={(e) =>
                                            setEditedTableData((prev) =>
                                              prev
                                                ? {
                                                    ...prev,
                                                    description: e.target.value,
                                                  }
                                                : null,
                                            )
                                          }
                                          placeholder="Describe what this table stores..."
                                          className="bg-surface text-white text-sm min-h-[48px]"
                                        />
                                      </div>
                                      <div className="flex gap-2">
                                        <Button
                                          type="button"
                                          size="sm"
                                          onClick={() =>
                                            handleSaveTable(selectedTableIndex!)
                                          }
                                          disabled={isSavingTable}
                                          className="h-7 text-xs bg-success hover:bg-success/90 text-button-text"
                                        >
                                          {isSavingTable ? (
                                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                          ) : (
                                            <Save className="h-3 w-3 mr-1" />
                                          )}
                                          {isSavingTable ? "Saving…" : "Save"}
                                        </Button>
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          onClick={() => {
                                            setEditingTable(null);
                                            setEditedTableData(null);
                                          }}
                                          disabled={isSavingTable}
                                          className="h-7 text-xs"
                                        >
                                          <X className="h-3 w-3 mr-1" />
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  )}

                                  {!isEditingSelectedTable &&
                                    selectedTable.description && (
                                      <div className="text-sm text-muted mb-2">
                                        {selectedTable.description}
                                      </div>
                                    )}

                                  {displayColumns.length > 0 ? (
                                    <div className="space-y-1.5">
                                      {displayColumns.map(
                                        (column, paginatedColIdx) => {
                                          const colIdx =
                                            columnsStartIndex + paginatedColIdx;
                                          const isEditingThisColumn =
                                            editingColumn?.tableIndex ===
                                              selectedTableIndex &&
                                            editingColumn?.columnIndex ===
                                              colIdx;
                                          return (
                                            <div
                                              key={
                                                column.id ??
                                                `${selectedTableIndex}-${colIdx}`
                                              }
                                              className="border border-surface-border rounded bg-surface p-2.5 group/column"
                                            >
                                              {isEditingThisColumn ? (
                                                <div className="space-y-2">
                                                  <div className="grid grid-cols-2 gap-2">
                                                    <div className="space-y-1">
                                                      <label className="text-xs font-medium text-muted">
                                                        Name
                                                      </label>
                                                      <Input
                                                        value={
                                                          editedColumnData?.name ||
                                                          ""
                                                        }
                                                        onChange={(e) =>
                                                          setEditedColumnData(
                                                            (prev) =>
                                                              prev
                                                                ? {
                                                                    ...prev,
                                                                    name: e
                                                                      .target
                                                                      .value,
                                                                  }
                                                                : null,
                                                          )
                                                        }
                                                        placeholder="Column name"
                                                        className="bg-surface-active text-white text-xs h-7"
                                                      />
                                                    </div>
                                                    <div className="space-y-1">
                                                      <label className="text-xs font-medium text-muted">
                                                        Type
                                                      </label>
                                                      <Input
                                                        value={
                                                          editedColumnData?.type ||
                                                          ""
                                                        }
                                                        onChange={(e) =>
                                                          setEditedColumnData(
                                                            (prev) =>
                                                              prev
                                                                ? {
                                                                    ...prev,
                                                                    type: e
                                                                      .target
                                                                      .value,
                                                                  }
                                                                : null,
                                                          )
                                                        }
                                                        placeholder="Data type"
                                                        className="bg-surface-active text-white text-xs h-7"
                                                      />
                                                    </div>
                                                  </div>
                                                  <div className="space-y-1">
                                                    <label className="text-xs font-medium text-muted">
                                                      Description
                                                    </label>
                                                    <Textarea
                                                      value={
                                                        editedColumnData?.description ||
                                                        ""
                                                      }
                                                      onChange={(e) =>
                                                        setEditedColumnData(
                                                          (prev) =>
                                                            prev
                                                              ? {
                                                                  ...prev,
                                                                  description:
                                                                    e.target
                                                                      .value,
                                                                }
                                                              : null,
                                                        )
                                                      }
                                                      placeholder="Column description"
                                                      className="bg-surface-active text-white text-xs min-h-[50px]"
                                                    />
                                                  </div>
                                                  <div className="flex gap-3">
                                                    <div className="flex items-center gap-2">
                                                      <Switch
                                                        checked={
                                                          editedColumnData?.primaryKey ||
                                                          false
                                                        }
                                                        onCheckedChange={(
                                                          checked,
                                                        ) =>
                                                          setEditedColumnData(
                                                            (prev) =>
                                                              prev
                                                                ? {
                                                                    ...prev,
                                                                    primaryKey:
                                                                      checked,
                                                                  }
                                                                : null,
                                                          )
                                                        }
                                                      />
                                                      <span className="text-xs text-muted">
                                                        Primary Key
                                                      </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                      <Switch
                                                        checked={
                                                          editedColumnData?.nullable ||
                                                          false
                                                        }
                                                        onCheckedChange={(
                                                          checked,
                                                        ) =>
                                                          setEditedColumnData(
                                                            (prev) =>
                                                              prev
                                                                ? {
                                                                    ...prev,
                                                                    nullable:
                                                                      checked,
                                                                  }
                                                                : null,
                                                          )
                                                        }
                                                      />
                                                      <span className="text-xs text-muted">
                                                        Nullable
                                                      </span>
                                                    </div>
                                                  </div>
                                                  <div className="flex gap-2 pt-1">
                                                    <Button
                                                      type="button"
                                                      size="sm"
                                                      onClick={() =>
                                                        handleSaveColumn(
                                                          selectedTableIndex!,
                                                          colIdx,
                                                        )
                                                      }
                                                      disabled={isSavingColumn}
                                                      className="h-7 text-xs bg-success hover:bg-success/90 text-button-text"
                                                    >
                                                      {isSavingColumn ? (
                                                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                                      ) : (
                                                        <Save className="h-3 w-3 mr-1" />
                                                      )}
                                                      {isSavingColumn
                                                        ? "Saving…"
                                                        : "Save"}
                                                    </Button>
                                                    <Button
                                                      type="button"
                                                      variant="outline"
                                                      size="sm"
                                                      onClick={() => {
                                                        setEditingColumn(null);
                                                        setEditedColumnData(
                                                          null,
                                                        );
                                                      }}
                                                      disabled={isSavingColumn}
                                                      className="h-7 text-xs"
                                                    >
                                                      <X className="h-3 w-3 mr-1" />
                                                      Cancel
                                                    </Button>
                                                  </div>
                                                </div>
                                              ) : (
                                                <div className="flex items-start justify-between gap-2">
                                                  <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                      <span className="text-sm font-medium text-white truncate">
                                                        {column.name}
                                                      </span>
                                                      <span className="text-xs text-muted shrink-0">
                                                        {column.type}
                                                      </span>
                                                    </div>
                                                    {column.description && (
                                                      <div className="text-xs text-muted mt-1">
                                                        {column.description}
                                                      </div>
                                                    )}
                                                  </div>
                                                  <div className="flex gap-1.5 shrink-0 items-center">
                                                    {column.primaryKey && (
                                                      <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded">
                                                        PK
                                                      </span>
                                                    )}
                                                    {column.nullable && (
                                                      <span className="text-xs bg-warning/20 text-warning px-1.5 py-0.5 rounded">
                                                        NULL
                                                      </span>
                                                    )}
                                                    {!isViewer && (
                                                      <div className="flex items-center gap-2 opacity-0 group-hover/column:opacity-100 transition-opacity ml-2">
                                                        <CustomTooltip text="Edit Column">
                                                          <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="icon"
                                                            onClick={() =>
                                                              handleEditColumn(
                                                                selectedTableIndex!,
                                                                colIdx,
                                                                column,
                                                              )
                                                            }
                                                          >
                                                            <Pencil className="h-4 w-4" />
                                                          </Button>
                                                        </CustomTooltip>
                                                        <CustomTooltip text="Delete Column">
                                                          <Button
                                                            type="button"
                                                            variant="destructive"
                                                            size="icon"
                                                            onClick={() =>
                                                              setPendingDataLakeDelete(
                                                                {
                                                                  type: "Column",
                                                                  name:
                                                                    column.name ||
                                                                    `Column ${colIdx + 1}`,
                                                                  execute: () =>
                                                                    handleDeleteColumn(
                                                                      selectedTableIndex!,
                                                                      colIdx,
                                                                      column,
                                                                    ),
                                                                },
                                                              )
                                                            }
                                                          >
                                                            <Trash2 className="h-4 w-4" />
                                                          </Button>
                                                        </CustomTooltip>
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        },
                                      )}
                                    </div>
                                  ) : isFetchingSelectedTableColumns ? (
                                    <div className="text-xs text-muted text-center py-2 flex items-center justify-center gap-2">
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                      Loading columns...
                                    </div>
                                  ) : (
                                    <div className="text-xs text-muted text-center py-2">
                                      No columns defined
                                    </div>
                                  )}

                                  {/* Columns Pagination — same shape as the Tables footer */}
                                  {columnsTotal > columnsPagination.limit && (
                                    <div className="flex items-center justify-between px-2 py-2 mt-3 bg-surface-active rounded border border-surface-border">
                                      <span className="text-xs text-info">
                                        Page {columnsPagination.page} of{" "}
                                        {columnsTotalPages} (
                                        {displayColumns.length} columns this
                                        page) • Total: {columnsTotal} columns
                                      </span>
                                      <div className="flex items-center gap-2">
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          onClick={() =>
                                            setColumnsPagination((prev) => ({
                                              ...prev,
                                              page: prev.page - 1,
                                            }))
                                          }
                                          disabled={
                                            columnsPagination.page === 1
                                          }
                                          className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
                                        >
                                          <ChevronLeft className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          onClick={() =>
                                            setColumnsPagination((prev) => ({
                                              ...prev,
                                              page: prev.page + 1,
                                            }))
                                          }
                                          disabled={
                                            columnsPagination.page ===
                                            columnsTotalPages
                                          }
                                          className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
                                        >
                                          <ChevronRight className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Indexes for the selected table */}
                              {selectedTable &&
                                (() => {
                                  const allIndexes = Array.isArray(
                                    dbOntologyIndexes,
                                  )
                                    ? dbOntologyIndexes
                                    : [];
                                  const total = allIndexes.length;
                                  const totalPages = Math.max(
                                    1,
                                    Math.ceil(total / indexesPagination.limit),
                                  );
                                  const start =
                                    (indexesPagination.page - 1) *
                                    indexesPagination.limit;
                                  const pageItems = allIndexes.slice(
                                    start,
                                    start + indexesPagination.limit,
                                  );

                                  return (
                                    <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-3">
                                          <h4 className="text-lg text-white font-semibold">
                                            Indexes
                                          </h4>
                                          <span className="text-sm text-muted bg-surface-dark px-1.5 py-0.5 rounded">
                                            {`${pageItems.length} of ${total}`}
                                          </span>
                                          <span className="text-sm text-white truncate">
                                            —{" "}
                                            {selectedTable.name ||
                                              `Table ${selectedTableIndex! + 1}`}
                                          </span>
                                          {isFetchingDbOntologyIndexes && (
                                            <Loader2 className="w-4 h-4 animate-spin text-muted" />
                                          )}
                                        </div>
                                        {!isViewer && (
                                          <CustomTooltip text="Add Index">
                                            <Button
                                              variant="outline"
                                              size="icon"
                                              disabled={!selectedTable.id}
                                              className="bg-primary hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (!selectedTable.id) {
                                                  toast.error(
                                                    "Save the table first before adding indexes",
                                                  );
                                                  return;
                                                }
                                                setEditingIndex(null);
                                                setIndexDialogOpen(true);
                                              }}
                                            >
                                              <Plus className="h-4 w-4" />
                                            </Button>
                                          </CustomTooltip>
                                        )}
                                      </div>

                                      {pageItems.length > 0 ? (
                                        <div className="grid grid-cols-1 gap-2">
                                          {pageItems.map((idx, i) => (
                                            <div
                                              key={idx.id ?? `${start + i}`}
                                              className="relative group px-3 py-2.5 rounded border border-surface-border bg-surface-active text-white hover:bg-surface-hover cursor-pointer"
                                              onClick={() => {
                                                setEditingIndex(idx);
                                                setIndexDialogOpen(true);
                                              }}
                                            >
                                              <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-base font-medium">
                                                  {idx.name ||
                                                    `Index ${start + i + 1}`}
                                                </span>
                                                {idx.indexType && (
                                                  <span className="text-xs text-muted">
                                                    ({idx.indexType})
                                                  </span>
                                                )}
                                                {idx.isUnique && (
                                                  <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded">
                                                    UNIQUE
                                                  </span>
                                                )}
                                                {Array.isArray(idx.columns) &&
                                                  idx.columns.length > 0 && (
                                                    <span className="text-xs text-muted">
                                                      on (
                                                      {idx.columns.join(", ")})
                                                    </span>
                                                  )}
                                              </div>
                                              {!isViewer && (
                                                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                  <CustomTooltip text="Delete Index">
                                                    <Button
                                                      variant="destructive"
                                                      size="icon"
                                                      disabled={!idx.id}
                                                      className="disabled:opacity-50 disabled:cursor-not-allowed"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (!idx.id) return;
                                                        setPendingDataLakeDelete(
                                                          {
                                                            type: "Index",
                                                            name:
                                                              idx.name ||
                                                              `Index ${start + i + 1}`,
                                                            execute:
                                                              async () => {
                                                                try {
                                                                  await deleteDbOntologyIndex.mutateAsync(
                                                                    {
                                                                      projectUuid,
                                                                      indexId:
                                                                        idx.id!,
                                                                      dataLakeId:
                                                                        selectedArchNode?.id,
                                                                    },
                                                                  );
                                                                  toast.success(
                                                                    "Index deleted successfully",
                                                                  );
                                                                } catch {
                                                                  // toast handled in mutation
                                                                }
                                                              },
                                                          },
                                                        );
                                                      }}
                                                    >
                                                      <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                  </CustomTooltip>
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      ) : isFetchingDbOntologyIndexes ? (
                                        <div className="text-xs text-muted text-center py-2 flex items-center justify-center gap-2">
                                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                          Loading indexes...
                                        </div>
                                      ) : (
                                        <div className="text-xs text-muted text-center py-2">
                                          No indexes defined. Click + to add
                                          one.
                                        </div>
                                      )}

                                      {total > indexesPagination.limit && (
                                        <div className="flex items-center justify-between px-2 py-2 mt-3 bg-surface-active rounded border border-surface-border">
                                          <span className="text-xs text-info">
                                            Page {indexesPagination.page} of{" "}
                                            {totalPages} ({pageItems.length}{" "}
                                            indexes this page) • Total: {total}{" "}
                                            indexes
                                          </span>
                                          <div className="flex items-center gap-2">
                                            <Button
                                              type="button"
                                              variant="outline"
                                              size="sm"
                                              onClick={() =>
                                                setIndexesPagination(
                                                  (prev) => ({
                                                    ...prev,
                                                    page: prev.page - 1,
                                                  }),
                                                )
                                              }
                                              disabled={
                                                indexesPagination.page === 1
                                              }
                                              className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
                                            >
                                              <ChevronLeft className="w-4 h-4" />
                                            </Button>
                                            <Button
                                              type="button"
                                              variant="outline"
                                              size="sm"
                                              onClick={() =>
                                                setIndexesPagination(
                                                  (prev) => ({
                                                    ...prev,
                                                    page: prev.page + 1,
                                                  }),
                                                )
                                              }
                                              disabled={
                                                indexesPagination.page ===
                                                totalPages
                                              }
                                              className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
                                            >
                                              <ChevronRight className="w-4 h-4" />
                                            </Button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}

                              {/* Constraints for the selected table */}
                              {selectedTable &&
                                (() => {
                                  const allCons = Array.isArray(
                                    dbOntologyConstraints,
                                  )
                                    ? dbOntologyConstraints
                                    : [];
                                  const total = allCons.length;
                                  const totalPages = Math.max(
                                    1,
                                    Math.ceil(
                                      total / constraintsPagination.limit,
                                    ),
                                  );
                                  const start =
                                    (constraintsPagination.page - 1) *
                                    constraintsPagination.limit;
                                  const pageItems = allCons.slice(
                                    start,
                                    start + constraintsPagination.limit,
                                  );

                                  return (
                                    <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-3">
                                          <h4 className="text-lg text-white font-semibold">
                                            Constraints
                                          </h4>
                                          <span className="text-sm text-muted bg-surface-dark px-1.5 py-0.5 rounded">
                                            {`${pageItems.length} of ${total}`}
                                          </span>
                                          <span className="text-sm text-white truncate">
                                            —{" "}
                                            {selectedTable.name ||
                                              `Table ${selectedTableIndex! + 1}`}
                                          </span>
                                          {isFetchingDbOntologyConstraints && (
                                            <Loader2 className="w-4 h-4 animate-spin text-muted" />
                                          )}
                                        </div>
                                        {!isViewer && (
                                          <CustomTooltip text="Add Constraint">
                                            <Button
                                              variant="outline"
                                              size="icon"
                                              disabled={!selectedTable.id}
                                              className="bg-primary hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (!selectedTable.id) {
                                                  toast.error(
                                                    "Save the table first before adding constraints",
                                                  );
                                                  return;
                                                }
                                                setEditingConstraint(null);
                                                setConstraintDialogOpen(true);
                                              }}
                                            >
                                              <Plus className="h-4 w-4" />
                                            </Button>
                                          </CustomTooltip>
                                        )}
                                      </div>

                                      {pageItems.length > 0 ? (
                                        <div className="grid grid-cols-1 gap-2">
                                          {pageItems.map((cons, i) => (
                                            <div
                                              key={cons.id ?? `${start + i}`}
                                              className="relative group px-3 py-2.5 rounded border border-surface-border bg-surface-active text-white hover:bg-surface-hover cursor-pointer"
                                              onClick={() => {
                                                setEditingConstraint(cons);
                                                setConstraintDialogOpen(true);
                                              }}
                                            >
                                              <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-base font-medium">
                                                  {cons.name ||
                                                    `Constraint ${start + i + 1}`}
                                                </span>
                                                {cons.constraintType && (
                                                  <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded">
                                                    {cons.constraintType}
                                                  </span>
                                                )}
                                                {Array.isArray(cons.columns) &&
                                                  cons.columns.length > 0 && (
                                                    <span className="text-xs text-muted">
                                                      on (
                                                      {cons.columns.join(", ")})
                                                    </span>
                                                  )}
                                              </div>
                                              {!isViewer && (
                                                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                  <CustomTooltip text="Delete Constraint">
                                                    <Button
                                                      variant="destructive"
                                                      size="icon"
                                                      disabled={!cons.id}
                                                      className="disabled:opacity-50 disabled:cursor-not-allowed"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (!cons.id) return;
                                                        setPendingDataLakeDelete(
                                                          {
                                                            type: "Constraint",
                                                            name:
                                                              cons.name ||
                                                              `Constraint`,
                                                            execute:
                                                              async () => {
                                                                try {
                                                                  await deleteDbOntologyConstraint.mutateAsync(
                                                                    {
                                                                      projectUuid,
                                                                      constraintId:
                                                                        cons.id!,
                                                                      dataLakeId:
                                                                        selectedArchNode?.id,
                                                                    },
                                                                  );
                                                                  toast.success(
                                                                    "Constraint deleted successfully",
                                                                  );
                                                                } catch {
                                                                  // toast handled in mutation
                                                                }
                                                              },
                                                          },
                                                        );
                                                      }}
                                                    >
                                                      <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                  </CustomTooltip>
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      ) : isFetchingDbOntologyConstraints ? (
                                        <div className="text-xs text-muted text-center py-2 flex items-center justify-center gap-2">
                                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                          Loading constraints...
                                        </div>
                                      ) : (
                                        <div className="text-xs text-muted text-center py-2">
                                          No constraints defined. Click + to add
                                          one.
                                        </div>
                                      )}

                                      {total > constraintsPagination.limit && (
                                        <div className="flex items-center justify-between px-2 py-2 mt-3 bg-surface-active rounded border border-surface-border">
                                          <span className="text-xs text-info">
                                            Page {constraintsPagination.page} of{" "}
                                            {totalPages} ({pageItems.length}{" "}
                                            constraints this page) • Total:{" "}
                                            {total} constraints
                                          </span>
                                          <div className="flex items-center gap-2">
                                            <Button
                                              type="button"
                                              variant="outline"
                                              size="sm"
                                              onClick={() =>
                                                setConstraintsPagination(
                                                  (prev) => ({
                                                    ...prev,
                                                    page: prev.page - 1,
                                                  }),
                                                )
                                              }
                                              disabled={
                                                constraintsPagination.page === 1
                                              }
                                              className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
                                            >
                                              <ChevronLeft className="w-4 h-4" />
                                            </Button>
                                            <Button
                                              type="button"
                                              variant="outline"
                                              size="sm"
                                              onClick={() =>
                                                setConstraintsPagination(
                                                  (prev) => ({
                                                    ...prev,
                                                    page: prev.page + 1,
                                                  }),
                                                )
                                              }
                                              disabled={
                                                constraintsPagination.page ===
                                                totalPages
                                              }
                                              className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
                                            >
                                              <ChevronRight className="w-4 h-4" />
                                            </Button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                            </>
                          );
                        })()}

                      {/* Views — DDL-only; hidden for Elastic and Non-Relational data lakes */}
                      {c === "data_lake" &&
                        expandedCategory === "data_lake" &&
                        selectedArchNode &&
                        selectedArchNode.group === "data_lake" &&
                        showDdlPanels &&
                        (() => {
                          // Server-paginated hook already returns just the current page.
                          const viewsPage = dbOntologyViewsPage?.items ?? [];
                          const viewsTotal =
                            dbOntologyViewsPage?.total ?? viewsPage.length;
                          const viewsTotalPages = Math.max(
                            1,
                            Math.ceil(viewsTotal / viewsPagination.limit),
                          );
                          const viewsStart =
                            (viewsPagination.page - 1) * viewsPagination.limit;

                          return (
                            <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                  <h4 className="text-lg text-white font-semibold">
                                    Views
                                  </h4>
                                  <span className="text-sm text-muted bg-surface-dark px-1.5 py-0.5 rounded">
                                    {`${viewsPage.length} of ${viewsTotal}`}
                                  </span>
                                  <span className="text-sm text-white truncate">
                                    —{" "}
                                    {String(
                                      selectedArchNode?.metadata?.blueprint
                                        ?.name ||
                                        selectedArchNode?.name ||
                                        "Unnamed",
                                    )}
                                  </span>
                                  {isFetchingDbOntologyViews && (
                                    <Loader2 className="w-4 h-4 animate-spin text-muted" />
                                  )}
                                </div>
                                {!isViewer && (
                                  <CustomTooltip text="Add View">
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      className="bg-primary hover:bg-primary-dark"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingView(null);
                                        setViewDialogOpen(true);
                                      }}
                                    >
                                      <Plus className="h-4 w-4" />
                                    </Button>
                                  </CustomTooltip>
                                )}
                              </div>

                              {viewsPage.length > 0 ? (
                                <div className="grid grid-cols-1 gap-2">
                                  {viewsPage.map((view, idx) => (
                                    <div
                                      key={view.id ?? `${viewsStart + idx}`}
                                      className="relative group px-3 py-2.5 rounded border border-surface-border bg-surface-active text-white hover:bg-surface-hover cursor-pointer"
                                      onClick={() => {
                                        setEditingView(view);
                                        setViewDialogOpen(true);
                                      }}
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className="text-base font-medium">
                                          {view.name ||
                                            `View ${viewsStart + idx + 1}`}
                                        </span>
                                        {view.viewType && (
                                          <span className="text-xs text-muted">
                                            ({view.viewType})
                                          </span>
                                        )}
                                      </div>
                                      {isViewer && (
                                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                          <CustomTooltip text="View">
                                            <Button
                                              variant="outline"
                                              size="icon"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setEditingView(view);
                                                setViewDialogOpen(true);
                                              }}
                                            >
                                              <Eye className="h-4 w-4" />
                                            </Button>
                                          </CustomTooltip>
                                        </div>
                                      )}
                                      {!isViewer && (
                                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                          <CustomTooltip text="Edit View">
                                            <Button
                                              variant="outline"
                                              size="icon"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setEditingView(view);
                                                setViewDialogOpen(true);
                                              }}
                                            >
                                              <Pencil className="h-4 w-4" />
                                            </Button>
                                          </CustomTooltip>
                                          <CustomTooltip text="Delete View">
                                            <Button
                                              variant="destructive"
                                              size="icon"
                                              disabled={!view.id}
                                              className="disabled:opacity-50 disabled:cursor-not-allowed"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (!view.id) return;
                                                setPendingDataLakeDelete({
                                                  type: "View",
                                                  name: view.name || `View`,
                                                  execute: async () => {
                                                    try {
                                                      await deleteDbOntologyView.mutateAsync(
                                                        {
                                                          projectUuid,
                                                          viewId: view.id!,
                                                          dataLakeId:
                                                            selectedArchNode?.id,
                                                        },
                                                      );
                                                      toast.success(
                                                        "View deleted successfully",
                                                      );
                                                    } catch {
                                                      // toast handled in mutation
                                                    }
                                                  },
                                                });
                                              }}
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </Button>
                                          </CustomTooltip>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : isFetchingDbOntologyViews ? (
                                <div className="text-xs text-muted text-center py-2 flex items-center justify-center gap-2">
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  Loading views...
                                </div>
                              ) : (
                                <div className="text-xs text-muted text-center py-2">
                                  No views defined. Click + to add one.
                                </div>
                              )}

                              {viewsTotal > viewsPagination.limit && (
                                <div className="flex items-center justify-between px-2 py-2 mt-3 bg-surface-active rounded border border-surface-border">
                                  <span className="text-sm text-info">
                                    Page {viewsPagination.page} of{" "}
                                    {viewsTotalPages} ({viewsPage.length} views
                                    this page) • Total: {viewsTotal} views
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        setViewsPagination((prev) => ({
                                          ...prev,
                                          page: prev.page - 1,
                                        }))
                                      }
                                      disabled={viewsPagination.page === 1}
                                      className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
                                    >
                                      <ChevronLeft className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        setViewsPagination((prev) => ({
                                          ...prev,
                                          page: prev.page + 1,
                                        }))
                                      }
                                      disabled={
                                        viewsPagination.page === viewsTotalPages
                                      }
                                      className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
                                    >
                                      <ChevronRight className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                      {/* Procedures — DDL-only; hidden for Elastic and Non-Relational data lakes */}
                      {c === "data_lake" &&
                        expandedCategory === "data_lake" &&
                        selectedArchNode &&
                        selectedArchNode.group === "data_lake" &&
                        showDdlPanels &&
                        (() => {
                          // Server-side pagination: hook returns the current page + total count.
                          const procsPage =
                            dbOntologyProceduresPage?.items ?? [];
                          const procsTotal =
                            dbOntologyProceduresPage?.total ?? procsPage.length;
                          const procsTotalPages = Math.max(
                            1,
                            Math.ceil(procsTotal / proceduresPagination.limit),
                          );
                          const procsStart =
                            (proceduresPagination.page - 1) *
                            proceduresPagination.limit;

                          return (
                            <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                  <h4 className="text-lg text-white font-semibold">
                                    Procedures
                                  </h4>
                                  <span className="text-sm text-muted bg-surface-dark px-1.5 py-0.5 rounded">
                                    {`${procsPage.length} of ${procsTotal}`}
                                  </span>
                                  <span className="text-sm text-white truncate">
                                    —{" "}
                                    {String(
                                      selectedArchNode?.metadata?.blueprint
                                        ?.name ||
                                        selectedArchNode?.name ||
                                        "Unnamed",
                                    )}
                                  </span>
                                  {isFetchingDbOntologyProcedures && (
                                    <Loader2 className="w-4 h-4 animate-spin text-muted" />
                                  )}
                                </div>
                                {!isViewer && (
                                  <CustomTooltip text="Add Procedure">
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      className="bg-primary hover:bg-primary-dark"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingProcedure(null);
                                        setProcedureDialogOpen(true);
                                      }}
                                    >
                                      <Plus className="h-4 w-4" />
                                    </Button>
                                  </CustomTooltip>
                                )}
                              </div>

                              {procsPage.length > 0 ? (
                                <div className="grid grid-cols-1 gap-2">
                                  {procsPage.map((proc, idx) => (
                                    <div
                                      key={proc.id ?? `${procsStart + idx}`}
                                      className="relative group px-3 py-2.5 rounded border border-surface-border bg-surface-active text-white hover:bg-surface-hover cursor-pointer"
                                      onClick={() => {
                                        // Row click opens the dialog. For viewers
                                        // it renders read-only; for editors it's
                                        // the existing edit flow.
                                        setEditingProcedure(proc);
                                        setProcedureDialogOpen(true);
                                      }}
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className="text-base font-medium">
                                          {proc.name ||
                                            `Procedure ${procsStart + idx + 1}`}
                                        </span>
                                        {proc.procedureType && (
                                          <span className="text-xs text-muted">
                                            ({proc.procedureType})
                                          </span>
                                        )}
                                      </div>
                                      {isViewer && (
                                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                          <CustomTooltip text="View Procedure">
                                            <Button
                                              variant="outline"
                                              size="icon"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setEditingProcedure(proc);
                                                setProcedureDialogOpen(true);
                                              }}
                                            >
                                              <Eye className="h-4 w-4" />
                                            </Button>
                                          </CustomTooltip>
                                        </div>
                                      )}
                                      {!isViewer && (
                                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                          <CustomTooltip text="Edit Procedure">
                                            <Button
                                              variant="outline"
                                              size="icon"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setEditingProcedure(proc);
                                                setProcedureDialogOpen(true);
                                              }}
                                            >
                                              <Pencil className="h-4 w-4" />
                                            </Button>
                                          </CustomTooltip>
                                          <CustomTooltip text="Delete Procedure">
                                            <Button
                                              variant="destructive"
                                              size="icon"
                                              disabled={!proc.id}
                                              className="disabled:opacity-50 disabled:cursor-not-allowed"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (!proc.id) return;
                                                setPendingDataLakeDelete({
                                                  type: "Procedure",
                                                  name:
                                                    proc.name || `Procedure`,
                                                  execute: async () => {
                                                    try {
                                                      await deleteDbOntologyProcedure.mutateAsync(
                                                        {
                                                          projectUuid,
                                                          procedureId: proc.id!,
                                                          dataLakeId:
                                                            selectedArchNode?.id,
                                                        },
                                                      );
                                                      toast.success(
                                                        "Procedure deleted successfully",
                                                      );
                                                    } catch {
                                                      // toast handled in mutation
                                                    }
                                                  },
                                                });
                                              }}
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </Button>
                                          </CustomTooltip>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : isFetchingDbOntologyProcedures ? (
                                <div className="text-xs text-muted text-center py-2 flex items-center justify-center gap-2">
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  Loading procedures...
                                </div>
                              ) : (
                                <div className="text-xs text-muted text-center py-2">
                                  No procedures defined. Click + to add one.
                                </div>
                              )}

                              {procsTotal > proceduresPagination.limit && (
                                <div className="flex items-center justify-between px-2 py-2 mt-3 bg-surface-active rounded border border-surface-border">
                                  <span className="text-sm text-info">
                                    Page {proceduresPagination.page} of{" "}
                                    {procsTotalPages} ({procsPage.length}{" "}
                                    procedures this page) • Total: {procsTotal}{" "}
                                    procedures
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        setProceduresPagination((prev) => ({
                                          ...prev,
                                          page: prev.page - 1,
                                        }))
                                      }
                                      disabled={proceduresPagination.page === 1}
                                      className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
                                    >
                                      <ChevronLeft className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        setProceduresPagination((prev) => ({
                                          ...prev,
                                          page: prev.page + 1,
                                        }))
                                      }
                                      disabled={
                                        proceduresPagination.page ===
                                        procsTotalPages
                                      }
                                      className="h-8 w-8 p-0 bg-surface-active border-surface-border text-white hover:bg-surface-hover disabled:opacity-50"
                                    >
                                      <ChevronRight className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                      {/* ES Indexes — only when the data lake type is Elastic */}
                      {c === "data_lake" &&
                        expandedCategory === "data_lake" &&
                        selectedArchNode &&
                        selectedArchNode.group === "data_lake" &&
                        selectedArchNode.id &&
                        isSelectedDataLakeElastic && (
                          <EsOntologySection
                            projectUuid={projectUuid}
                            dataLakeId={String(selectedArchNode.id)}
                            dataLakeLabel={String(
                              (selectedArchNode as any)?.metadata?.blueprint
                                ?.name ||
                                (selectedArchNode as any)?.name ||
                                "",
                            )}
                            isViewer={isViewer}
                          />
                        )}

                      {/* Collections — only when the data lake type is Non-Relational */}
                      {c === "data_lake" &&
                        expandedCategory === "data_lake" &&
                        selectedArchNode &&
                        selectedArchNode.group === "data_lake" &&
                        selectedArchNode.id &&
                        isSelectedDataLakeNonRelational && (
                          <NoSqlOntologySection
                            projectUuid={projectUuid}
                            dataLakeId={String(selectedArchNode.id)}
                            dataLakeLabel={String(
                              (selectedArchNode as any)?.metadata?.blueprint
                                ?.name ||
                                (selectedArchNode as any)?.name ||
                                "",
                            )}
                            isViewer={isViewer}
                          />
                        )}

                      {/* Graph database schema — only when the data lake type is Graph */}
                      {c === "data_lake" &&
                        expandedCategory === "data_lake" &&
                        selectedArchNode &&
                        selectedArchNode.group === "data_lake" &&
                        selectedArchNode.id &&
                        isSelectedDataLakeGraph && (
                          <GraphDbOntologySection
                            key={String(selectedArchNode.id)}
                            projectUuid={projectUuid}
                            dataLakeId={String(selectedArchNode.id)}
                            dataLakeLabel={String(
                              (selectedArchNode as any)?.metadata?.blueprint
                                ?.name ||
                                (selectedArchNode as any)?.name ||
                                "",
                            )}
                            isViewer={isViewer}
                          />
                        )}
                    </AccordionContent>
                  </AccordionItem>
                </Fragment>
              );
            })}
          </Accordion>
        </>
      )}

      {popupOpen && selectedComponent && (
        <ArchitecturePopupEditor
          open={popupOpen}
          onOpenChange={setPopupOpen}
          component={selectedComponent}
          onSave={handlePopupSave}
          allArchitectureData={Object.values(currentPageData).flat()}
        />
      )}

      <ArchitectureNodeDialog
        isOpen={isNodeDialogOpen}
        onClose={handleCloseNodeDialog}
        onSubmit={handleNodeSubmit}
        node={currentNode}
        category={currentCategory}
      />

      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Confirm Deletion"
        description="Are you sure you want to delete this component? This action cannot be undone."
      />

      <ConfirmDialog
        open={isEmbeddingConfirmOpen}
        onOpenChange={() => setEmbeddingConfirmOpen(false)}
        onConfirm={handleConfirmRegenerateEmbeddings}
        title="Confirm Embedding Regeneration"
        description="Re-generating embeddings is an expensive operation that will recalculate all semantic embeddings for this project's architecture. This process may take several minutes. Are you sure you want to proceed?"
      />

      {/* Delete confirmation for Data Lake Table / Column / Index rows. The dialog
          auto-closes on Confirm, which also prevents the user from spamming the
          destructive click (BREEZEAI-793 Issue 1). */}
      <ConfirmDialog
        open={pendingDataLakeDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDataLakeDelete(null);
        }}
        onConfirm={() => {
          const pending = pendingDataLakeDelete;
          if (!pending) return;
          Promise.resolve(pending.execute()).catch(() => {
            // handler already surfaces its own toast
          });
          setPendingDataLakeDelete(null);
        }}
        onCancel={() => setPendingDataLakeDelete(null)}
        title={
          pendingDataLakeDelete
            ? `Delete ${pendingDataLakeDelete.type}?`
            : "Delete?"
        }
        description={
          pendingDataLakeDelete
            ? `Are you sure you want to delete ${pendingDataLakeDelete.type.toLowerCase()} "${pendingDataLakeDelete.name}"? This action cannot be undone.`
            : "This action cannot be undone."
        }
        confirmText="Delete"
      />

      {isCreateTableOpen && dataLakeNodeId && (
        <DbOntologyTableDialog
          isOpen={isCreateTableOpen}
          onClose={() => setCreateTableOpen(false)}
          projectUuid={projectUuid}
          dataLakeId={dataLakeNodeId}
        />
      )}

      {isViewDialogOpen && dataLakeNodeId && (
        <DbOntologyViewDialog
          isOpen={isViewDialogOpen}
          onClose={() => {
            setViewDialogOpen(false);
            setEditingView(null);
          }}
          projectUuid={projectUuid}
          dataLakeId={dataLakeNodeId}
          dataLakeLabel={String(
            selectedArchNode?.metadata?.blueprint?.name ||
              selectedArchNode?.name ||
              "",
          )}
          view={editingView}
          isReadOnly={isViewer}
          onSaved={() => {
            if (!editingView) setViewsPagination({ page: 1, limit: 5 });
          }}
        />
      )}

      {isProcedureDialogOpen && dataLakeNodeId && (
        <DbOntologyProcedureDialog
          isOpen={isProcedureDialogOpen}
          onClose={() => {
            setProcedureDialogOpen(false);
            setEditingProcedure(null);
          }}
          projectUuid={projectUuid}
          dataLakeId={dataLakeNodeId}
          dataLakeLabel={String(
            selectedArchNode?.metadata?.blueprint?.name ||
              selectedArchNode?.name ||
              "",
          )}
          procedure={editingProcedure}
          isReadOnly={isViewer}
          onSaved={() => {
            if (!editingProcedure)
              setProceduresPagination({ page: 1, limit: 5 });
          }}
        />
      )}

      {isIndexDialogOpen && dataLakeNodeId && (
        <DbOntologyIndexDialog
          isOpen={isIndexDialogOpen}
          onClose={() => {
            setIndexDialogOpen(false);
            setEditingIndex(null);
          }}
          projectUuid={projectUuid}
          dataLakeId={dataLakeNodeId}
          dataLakeLabel={String(
            selectedArchNode?.metadata?.blueprint?.name ||
              selectedArchNode?.name ||
              "",
          )}
          tables={dbOntologyTablesForRef?.items ?? []}
          index={editingIndex}
          defaultTableId={selectedTableNodeId}
          isReadOnly={isViewer}
          onSaved={() => {
            if (!editingIndex) setIndexesPagination({ page: 1, limit: 5 });
          }}
        />
      )}

      {isConstraintDialogOpen && dataLakeNodeId && (
        <DbOntologyConstraintDialog
          isOpen={isConstraintDialogOpen}
          onClose={() => {
            setConstraintDialogOpen(false);
            setEditingConstraint(null);
          }}
          projectUuid={projectUuid}
          dataLakeId={dataLakeNodeId}
          dataLakeLabel={String(
            selectedArchNode?.metadata?.blueprint?.name ||
              selectedArchNode?.name ||
              "",
          )}
          tables={dbOntologyTablesForRef?.items ?? []}
          constraint={editingConstraint}
          defaultTableId={selectedTableNodeId}
          isReadOnly={isViewer}
          onSaved={() => {
            if (!editingConstraint)
              setConstraintsPagination({ page: 1, limit: 5 });
          }}
        />
      )}

      {isCreateColumnOpen && dataLakeNodeId && selectedTableForColumns?.id && (
        <DbOntologyColumnDialog
          isOpen={isCreateColumnOpen}
          onClose={() => setCreateColumnOpen(false)}
          projectUuid={projectUuid}
          dataLakeId={dataLakeNodeId}
          tableId={selectedTableForColumns.id}
          tableLabel={selectedTableForColumns.name}
          existingColumnsCount={
            Array.isArray(selectedTableColumns)
              ? selectedTableColumns.length
              : 0
          }
          onCreated={() => {
            setColumnsPagination({ page: 1, limit: 5 });
            setTimeout(() => {
              loadCategoryDataInternal(
                "data_lake",
                categoryPagination["data_lake"]?.page || 1,
              );
            }, 300);
          }}
        />
      )}
    </div>
  );
}
